import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { useSSE } from "../hooks/useSSE";
import { AuthContext } from "../App";
import { toast } from "sonner";
import {
  Map, List, Briefcase, BookOpen, Settings2,
  Plus, Pencil, Trash2, X, Save, Loader2,
  ChevronDown, ChevronUp, CheckCircle2, AlertTriangle,
  Home, Users, FileText, ArrowLeft, RefreshCw, Eye,
  Building2, LayoutGrid, MapPin, DollarSign, Calendar,
  Clock, TrendingUp, Package, Archive,
} from "lucide-react";
import { AuditoriaRegistroBtn } from "../components/AuditoriaModal";
import { EntidadModal } from "./CatastroPage";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const hoy = () => new Date().toISOString().split("T")[0];
const fmtNum = (n) => (Number(n) || 0).toLocaleString("es-PY");
const fmtGs  = (n) => `Gs. ${fmtNum(n)}`;
function CuentaOptions({ cuentas = [] }) {
  const labels = { PYG: "Cuenta bancaria Guaraníes (PYG)", USD: "Cuenta bancaria Dólares (USD)", BRL: "Cuenta bancaria Reales (BRL)", ARS: "Cuenta bancaria Pesos (ARS)" };
  const activas = (cuentas || []).filter(c => c.activa !== false);
  const monedasBase = ["PYG", "USD", "BRL", "ARS"];
  const monedasExtra = Array.from(new Set(activas.map(c => c.moneda || "PYG"))).filter(m => !monedasBase.includes(m));
  return (
    <>
      {[...monedasBase, ...monedasExtra].map(moneda => {
        const grupo = activas.filter(c => (c.moneda || "PYG") === moneda);
        if (!grupo.length) return null;
        return (
          <optgroup key={moneda} label={labels[moneda] || moneda}>
            {grupo.map(c => (
              <option key={c.id} value={c.id}>
                {c.nombre}{c.banco ? ` · ${c.banco}` : ""} ({c.moneda || "PYG"}){c.es_predeterminada ? " ★" : ""}
              </option>
            ))}
          </optgroup>
        );
      })}
    </>
  );
}

const ESTADO_COLOR = {
  disponible: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  reservado:  "text-amber-400  bg-amber-400/10  border-amber-400/20",
  vendido:    "text-rose-400   bg-rose-400/10   border-rose-400/20",
};
const ESTADO_LABEL = { disponible: "Disponible", reservado: "Reservado", vendido: "Vendido" };
const VENTA_ESTADO_COLOR = {
  activo:    "text-blue-400  bg-blue-400/10  border-blue-400/20",
  completado:"text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  cancelado: "text-slate-400 bg-slate-400/10 border-slate-400/20",
};

const LEAFLET_CSS_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS_URL = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
let leafletPromise = null;

const ensureLeaflet = () => {
  if (typeof window === "undefined") return Promise.reject(new Error("Mapa no disponible"));
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;

  if (!document.querySelector(`link[href="${LEAFLET_CSS_URL}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = LEAFLET_CSS_URL;
    document.head.appendChild(link);
  }

  leafletPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${LEAFLET_JS_URL}"]`);
    const script = existing || document.createElement("script");

    const handleLoad = () => window.L ? resolve(window.L) : reject(new Error("No se pudo cargar el mapa"));
    const handleError = () => reject(new Error("No se pudo cargar Leaflet"));

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });

    if (!existing) {
      script.src = LEAFLET_JS_URL;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return leafletPromise;
};

// ── Estilos base ──────────────────────────────────────────────────────────────
const INPUT  = "w-full bg-arandu-dark-light border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 placeholder-slate-500";
const LABEL  = "block text-xs text-slate-400 mb-1 uppercase tracking-wider";
const BTN_PRI = "flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_SEC = "flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-sm rounded-lg transition-colors";
const BTN_DNG = "flex items-center gap-2 px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-400 text-xs rounded-lg transition-colors";

const leerError = async (response, fallback = "Error") => {
  const text = await response.text().catch(() => "");
  if (!text) return fallback;
  try {
    const data = JSON.parse(text);
    if (Array.isArray(data.detail)) return data.detail.map(d => d.msg || d.detail).filter(Boolean).join(". ") || fallback;
    return data.detail || data.message || fallback;
  } catch {
    return text.startsWith("Internal Server Error") ? "Error interno del servidor" : text;
  }
};

const parseCoordenada = (value, tipo) => {
  if (value == null || String(value).trim() === "") return null;
  const raw = String(value).trim().replace(",", ".");
  let num = Number(raw);
  const limite = tipo === "latitud" ? 90 : 180;
  if (Number.isFinite(num) && Math.abs(num) <= limite) return num;

  if (!raw.includes(".")) {
    const sign = raw.startsWith("-") ? -1 : 1;
    const digits = raw.replace(/\D/g, "");
    for (const headLen of [2, 3]) {
      if (digits.length > headLen) {
        const candidate = sign * Number(`${digits.slice(0, headLen)}.${digits.slice(headLen)}`);
        if (Number.isFinite(candidate) && Math.abs(candidate) <= limite) return candidate;
      }
    }
  }

  throw new Error(`${tipo === "latitud" ? "Latitud" : "Longitud"} inválida. Usá un valor entre ${tipo === "latitud" ? "-90 y 90" : "-180 y 180"}.`);
};

const asArray = (value) => Array.isArray(value) ? value : [];
const asNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};
const normalizarStats = (value) => ({
  total: asNumber(value?.total),
  disponibles: asNumber(value?.disponibles),
  reservados: asNumber(value?.reservados),
  vendidos: asNumber(value?.vendidos),
  cuotas_vencidas: asNumber(value?.cuotas_vencidas),
  saldo_pendiente: asNumber(value?.saldo_pendiente),
});

const puntoValido = (pt) => Array.isArray(pt) && pt.length >= 2 && Number.isFinite(Number(pt[0])) && Number.isFinite(Number(pt[1]));
const geometriaValida = (geo) => Array.isArray(geo) ? geo.filter(puntoValido).map(pt => [Number(pt[0]), Number(pt[1])]) : [];
const centroGeometria = (geo) => {
  const pts = geometriaValida(geo);
  if (!pts.length) return null;
  const sum = pts.reduce((acc, pt) => ({ lat: acc.lat + pt[0], lng: acc.lng + pt[1] }), { lat: 0, lng: 0 });
  return { lat: sum.lat / pts.length, lng: sum.lng / pts.length };
};

// Structec Mapas (catastro, hidrografía, imágenes aéreas): se abre en pestaña nueva.
const STRUCTEC_MAPA_URL = "https://mapas.structec.com.py/mapa";
const abrirStructec = (centro) => {
  const lat = Number(centro?.lat), lng = Number(centro?.lng);
  const tieneCoord = Number.isFinite(lat) && Number.isFinite(lng);
  if (tieneCoord) {
    try { navigator.clipboard?.writeText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`); } catch {}
    toast.success(`Coordenadas copiadas (${lat.toFixed(5)}, ${lng.toFixed(5)}). Pegalas en el buscador del mapa si no se centra solo.`);
  }
  const qs = tieneCoord ? `?lat=${lat}&lng=${lng}&zoom=${centro?.zoom || 16}` : "";
  window.open(`${STRUCTEC_MAPA_URL}${qs}`, "_blank", "noopener");
};

class LoteadoraErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Mantiene visible el módulo aunque un dato legado rompa una tarjeta puntual.
    console.error("Error en LoteadoraPage", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-arandu-dark text-white flex items-center justify-center p-6">
          <div className="max-w-lg rounded-2xl border border-rose-500/20 bg-rose-500/10 p-5 text-center">
            <AlertTriangle className="w-8 h-8 text-rose-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold">No se pudo abrir Loteadora</h2>
            <p className="mt-2 text-sm text-rose-100/80">Hay un dato del módulo con formato inválido. Actualizá la página; si persiste, revisamos ese registro puntual.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Modal base ────────────────────────────────────────────────────────────────
function Modal({ title, icon, onClose, wide, children }) {
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className={`bg-arandu-dark border border-white/10 rounded-2xl shadow-2xl flex flex-col max-h-[90vh] ${wide ? "w-full max-w-2xl" : "w-full max-w-lg"}`}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            {icon}
            <h2 className="text-base font-semibold text-white">{title}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, half }) {
  return (
    <div className={half ? "flex-1 min-w-0" : "w-full"}>
      <label className={LABEL}>{label}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div className="flex gap-3">{children}</div>;
}

function ClienteLoteadoraSelect({ value, onChange, clientes = [], token, logo, onClienteCreado, label = "Cliente *", documentoLabel = "RUC / CI" }) {
  const [open, setOpen] = useState(false);
  const [crearNombre, setCrearNombre] = useState("");

  const texto = value?.nombre || "";
  const listaClientes = useMemo(() => Array.isArray(clientes) ? clientes : [], [clientes]);
  const selected = value?.id ? (listaClientes.find(c => c.id === value.id) || value) : null;
  const matches = useMemo(() => {
    const q = texto.trim().toLowerCase();
    const base = q
      ? listaClientes.filter(c =>
          (c.nombre || "").toLowerCase().includes(q) ||
          (c.razon_social || "").toLowerCase().includes(q) ||
          (c.ruc || "").toLowerCase().includes(q)
        )
      : listaClientes;
    return base.slice(0, 8);
  }, [listaClientes, texto]);

  const exact = texto.trim()
    ? listaClientes.some(c => (c.nombre || "").trim().toLowerCase() === texto.trim().toLowerCase() || (c.ruc || "").trim() === texto.trim())
    : true;

  const seleccionar = (cliente) => {
    onChange({
      id: cliente.id || "",
      nombre: cliente.nombre || cliente.razon_social || "",
      ruc: cliente.ruc || "",
      telefono: cliente.telefono || "",
    });
    setOpen(false);
  };

  const abrirCrear = () => {
    setCrearNombre(texto.trim());
    setOpen(false);
  };

  const handleSaved = (cliente) => {
    setCrearNombre("");
    if (cliente?.id) {
      onClienteCreado?.(cliente);
      seleccionar(cliente);
    }
  };

  return (
    <div className="relative">
      <label className={LABEL}>{label}</label>
      {selected ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-emerald-100">{selected.nombre || selected.razon_social}</p>
            <p className="text-xs text-emerald-100/70">{selected.ruc ? `${documentoLabel}: ${selected.ruc}` : "Cliente de Catastro"}</p>
          </div>
          <button
            type="button"
            onClick={() => onChange({ id: "", nombre: "", ruc: "", telefono: "" })}
            className="text-emerald-100/70 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <input
            className={INPUT}
            value={texto}
            onFocus={() => setOpen(true)}
            onChange={e => {
              onChange({ id: "", nombre: e.target.value, ruc: value?.ruc || "", telefono: value?.telefono || "" });
              setOpen(true);
            }}
            placeholder="Buscar cliente del Catastro..."
          />
          {open && (
            <div className="absolute z-[2100] mt-2 w-full overflow-hidden rounded-xl border border-white/10 bg-arandu-dark shadow-2xl">
              <div className="max-h-64 overflow-y-auto py-1">
                {matches.map(cliente => (
                  <button
                    key={cliente.id}
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => seleccionar(cliente)}
                    className="block w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/10"
                  >
                    <span className="block font-medium">{cliente.nombre || cliente.razon_social}</span>
                    <span className="block text-xs text-slate-400">{cliente.ruc || cliente.telefono || "Cliente"}</span>
                  </button>
                ))}
                {texto.trim() && !exact && (
                  <button
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={abrirCrear}
                    className="flex w-full items-center gap-2 border-t border-white/10 px-3 py-2 text-left text-sm text-blue-300 hover:bg-blue-500/10"
                  >
                    <Plus className="h-4 w-4" />
                    Crear "{texto.trim()}" como cliente
                  </button>
                )}
                {!matches.length && !texto.trim() && (
                  <p className="px-3 py-3 text-sm text-slate-400">No hay clientes cargados en Catastro.</p>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {crearNombre && (
        <EntidadModal
          entidad={{ _presetRol: "es_cliente", _initialNombre: crearNombre, _initialRazonSocial: crearNombre }}
          token={token}
          logo={logo}
          allowedRoles={{ es_cliente: true }}
          onClose={() => setCrearNombre("")}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ── Mapa Leaflet ──────────────────────────────────────────────────────────────
function MapaLotes({ lotes, loteamientos = [], loteamientoActivo, onLoteClick, defaultCenter }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const layersRef = useRef([]);
  const [mapError, setMapError] = useState("");

  const centerFor = useCallback(() => {
    const lotActivo = loteamientos.find(l => l.id === loteamientoActivo);
    const geoCenter = centroGeometria(lotActivo?.geometria);
    if (geoCenter) {
      return { lat: geoCenter.lat, lng: geoCenter.lng, zoom: lotActivo?.zoom || defaultCenter?.zoom || 16 };
    }
    const lotLat = Number(lotActivo?.lat_centro);
    const lotLng = Number(lotActivo?.lng_centro);
    if (Number.isFinite(lotLat) && Number.isFinite(lotLng)) {
      return { lat: lotLat, lng: lotLng, zoom: lotActivo.zoom || defaultCenter?.zoom || 16 };
    }
    const defLat = Number(defaultCenter?.lat);
    const defLng = Number(defaultCenter?.lng);
    if (Number.isFinite(defLat) && Number.isFinite(defLng)) return { lat: defLat, lng: defLng, zoom: defaultCenter?.zoom || 13 };
    return { lat: -25.2867, lng: -57.6470, zoom: 13 };
  }, [loteamientos, loteamientoActivo, defaultCenter]);

  const fallbackPoint = useCallback((lote, idx) => {
    const lat = Number(lote.lat);
    const lng = Number(lote.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
    const latCentro = Number(lote.lat_centro);
    const lngCentro = Number(lote.lng_centro);
    if (Number.isFinite(latCentro) && Number.isFinite(lngCentro)) return [latCentro, lngCentro];
    const lot = loteamientos.find(l => l.id === lote.loteamiento_id);
    const geoCenter = centroGeometria(lot?.geometria);
    const baseLat = Number(geoCenter?.lat ?? lot?.lat_centro ?? defaultCenter?.lat ?? -25.2867);
    const baseLng = Number(geoCenter?.lng ?? lot?.lng_centro ?? defaultCenter?.lng ?? -57.6470);
    if (!Number.isFinite(baseLat) || !Number.isFinite(baseLng)) return [-25.2867, -57.6470];
    const ring = Math.floor(idx / 8) + 1;
    const angle = (idx % 8) * (Math.PI / 4);
    const offset = 0.00018 * ring;
    return [
      Number(baseLat) + Math.sin(angle) * offset,
      Number(baseLng) + Math.cos(angle) * offset,
    ];
  }, [loteamientos, defaultCenter]);

  useEffect(() => {
    if (mapInstance.current) return;
    let cancelled = false;

    ensureLeaflet()
      .then((L) => {
        if (cancelled || !mapRef.current || mapInstance.current) return;
        try {
          const container = mapRef.current;
          if (container._leaflet_id) delete container._leaflet_id;
          setMapError("");
          const center = centerFor();
          const map = L.map(container, { center: [center.lat, center.lng], zoom: center.zoom });
          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "© OpenStreetMap contributors",
          }).addTo(map);
          mapInstance.current = map;
          setTimeout(() => {
            try { map.invalidateSize(); } catch {}
          }, 150);
        } catch {
          setMapError("No se pudo iniciar el mapa. Los lotes siguen disponibles en la lista.");
        }
      })
      .catch(() => {
        if (!cancelled) setMapError("No se pudo cargar el mapa. Actualizá la vista e intentá de nuevo.");
      });

    return () => {
      cancelled = true;
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapInstance.current) return;
    const center = centerFor();
    if (center?.lat != null && center?.lng != null) {
      mapInstance.current.setView([center.lat, center.lng], center.zoom || mapInstance.current.getZoom());
    }
  }, [centerFor]);

  // Dibujar lotes cuando cambian
  useEffect(() => {
    const L = typeof window !== "undefined" ? window.L : null;
    if (!mapInstance.current || !L) return;

    // Limpiar capas anteriores
    layersRef.current.forEach(l => {
      try { mapInstance.current.removeLayer(l); } catch {}
    });
    layersRef.current = [];

    const lotesAMostrar = loteamientoActivo
      ? lotes.filter(l => l.loteamiento_id === loteamientoActivo)
      : lotes;
    const loteamientosAMostrar = loteamientoActivo
      ? loteamientos.filter(l => l.id === loteamientoActivo)
      : loteamientos;

    const colorPorEstado = { disponible: "#34d399", reservado: "#fbbf24", vendido: "#f87171" };
    const bounds = [];

    loteamientosAMostrar.forEach(lot => {
      try {
        const geo = geometriaValida(lot.geometria);
        if (geo.length < 3) return;
        const area = L.polygon(geo, {
          color: "#60a5fa",
          weight: 3,
          fillColor: "#60a5fa",
          fillOpacity: 0.08,
          dashArray: "8 6",
        });
        area.bindTooltip(`<b>${lot.nombre || "Loteamiento"}</b><br>Área del loteamiento`, { sticky: true });
        area.addTo(mapInstance.current);
        layersRef.current.push(area);
        geo.forEach(pt => bounds.push(pt));
      } catch {}
    });

    lotesAMostrar.forEach((lote, idx) => {
      try {
        const geo = geometriaValida(lote.geometria);
        const color = colorPorEstado[lote.estado] || "#94a3b8";

        if (geo.length >= 3) {
          const poly = L.polygon(geo, {
            color, weight: 2, fillColor: color, fillOpacity: 0.35,
          });
          poly.bindTooltip(
            `<b>Mza ${lote.manzana || "-"} - Lote ${lote.numero || "-"}</b><br>${ESTADO_LABEL[lote.estado] || lote.estado || ""}<br>${fmtGs(lote.precio_lista)}`,
            { sticky: true }
          );
          poly.on("click", () => onLoteClick && onLoteClick(lote));
          poly.addTo(mapInstance.current);
          layersRef.current.push(poly);
          geo.forEach(pt => bounds.push(pt));
        } else {
          const point = fallbackPoint(lote, idx);
          if (!point) return;
          const marker = L.circleMarker(point, {
            radius: 9,
            color,
            weight: 2,
            fillColor: color,
            fillOpacity: 0.55,
          });
          marker.bindTooltip(
            `<b>Mza ${lote.manzana || "-"} - Lote ${lote.numero || "-"}</b><br>${ESTADO_LABEL[lote.estado] || lote.estado || ""}<br>${fmtGs(lote.precio_lista)}<br><small>Sin geometría exacta</small>`,
            { sticky: true }
          );
          marker.on("click", () => onLoteClick && onLoteClick(lote));
          marker.addTo(mapInstance.current);
          layersRef.current.push(marker);
          bounds.push(point);
        }
      } catch {}
    });

    if (bounds.length > 0) {
      try { mapInstance.current.fitBounds(bounds, { padding: [30, 30] }); } catch {}
    } else {
      const center = centerFor();
      if (center?.lat != null && center?.lng != null) {
        mapInstance.current.setView([center.lat, center.lng], center.zoom || mapInstance.current.getZoom());
      }
    }
  }, [lotes, loteamientos, loteamientoActivo, fallbackPoint, centerFor, onLoteClick]);

  return (
    <div className="relative">
      <div ref={mapRef} className="w-full rounded-xl overflow-hidden border border-white/10" style={{ height: 420, background: "#1e293b" }} />
      {mapError && (
        <div className="absolute inset-0 flex items-center justify-center rounded-xl border border-rose-500/20 bg-slate-900/90 px-4 text-center text-sm text-rose-200">
          {mapError}
        </div>
      )}
    </div>
  );
}

function EditorAreaLoteamiento({ value = [], center, zoom = 16, onChange }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const layersRef = useRef([]);
  const valueRef = useRef(value);
  const [mapError, setMapError] = useState("");

  useEffect(() => { valueRef.current = value; }, [value]);

  const centerFor = useCallback(() => {
    const geoCenter = centroGeometria(value);
    if (geoCenter) return geoCenter;
    if (center?.lat != null && center?.lng != null) return center;
    return { lat: -25.2867, lng: -57.6470 };
  }, [value, center]);

  useEffect(() => {
    if (mapInstance.current) return;
    let cancelled = false;

    ensureLeaflet()
      .then((L) => {
        if (cancelled || !mapRef.current || mapInstance.current) return;
        try {
          const container = mapRef.current;
          if (container._leaflet_id) delete container._leaflet_id;
          setMapError("");
          const c = centerFor();
          const map = L.map(container, { center: [c.lat, c.lng], zoom: zoom || 16 });
          L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "© OpenStreetMap contributors",
          }).addTo(map);
          map.on("click", e => {
            const next = [...geometriaValida(valueRef.current), [e.latlng.lat, e.latlng.lng]];
            onChange(next);
          });
          mapInstance.current = map;
          setTimeout(() => {
            try { map.invalidateSize(); } catch {}
          }, 150);
        } catch {
          setMapError("No se pudo iniciar el editor del mapa.");
        }
      })
      .catch(() => {
        if (!cancelled) setMapError("No se pudo cargar el editor del mapa.");
      });

    return () => {
      cancelled = true;
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const L = typeof window !== "undefined" ? window.L : null;
    if (!mapInstance.current || !L) return;
    layersRef.current.forEach(layer => {
      try { mapInstance.current.removeLayer(layer); } catch {}
    });
    layersRef.current = [];

    const pts = geometriaValida(value);
    if (pts.length >= 3) {
      const poly = L.polygon(pts, {
        color: "#34d399",
        weight: 3,
        fillColor: "#34d399",
        fillOpacity: 0.14,
      }).addTo(mapInstance.current);
      layersRef.current.push(poly);
    }
    if (pts.length >= 2) {
      const line = L.polyline(pts, { color: "#93c5fd", weight: 2, dashArray: "5 5" }).addTo(mapInstance.current);
      layersRef.current.push(line);
    }
    pts.forEach((pt, idx) => {
      const marker = L.circleMarker(pt, {
        radius: 6,
        color: "#e0f2fe",
        weight: 2,
        fillColor: "#2563eb",
        fillOpacity: 0.9,
      }).bindTooltip(`Punto ${idx + 1}`, { permanent: false }).addTo(mapInstance.current);
      layersRef.current.push(marker);
    });

    if (pts.length > 0) {
      try { mapInstance.current.fitBounds(pts, { padding: [24, 24] }); } catch {}
    } else {
      const c = centerFor();
      mapInstance.current.setView([c.lat, c.lng], zoom || 16);
    }
  }, [value, centerFor, zoom]);

  return (
    <div className="space-y-3">
      <div className="relative">
        <div ref={mapRef} className="w-full h-72 rounded-xl overflow-hidden border border-white/10 bg-slate-800" />
        {mapError && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl border border-rose-500/20 bg-slate-900/90 px-4 text-center text-sm text-rose-200">
            {mapError}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-400">{geometriaValida(value).length} puntos marcados</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onChange(geometriaValida(value).slice(0, -1))}
            disabled={geometriaValida(value).length === 0}
            className={BTN_SEC}
          >
            Deshacer punto
          </button>
          <button
            type="button"
            onClick={() => onChange([])}
            disabled={geometriaValida(value).length === 0}
            className={BTN_DNG}
          >
            <Trash2 className="w-4 h-4" /> Limpiar área
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Badge estado ──────────────────────────────────────────────────────────────
function EstadoBadge({ estado }) {
  const cls = ESTADO_COLOR[estado] || "text-slate-400 bg-slate-400/10 border-slate-400/20";
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${cls}`}>
      {ESTADO_LABEL[estado] || estado}
    </span>
  );
}

// ── Cuota badge ───────────────────────────────────────────────────────────────
function CuotaBadge({ c }) {
  const esVencida = c.estado === "pendiente" && c.fecha_vencimiento < hoy();
  if (c.estado === "pagada") return <span className="text-[10px] bg-emerald-400/10 text-emerald-400 px-1.5 py-0.5 rounded">Pagada</span>;
  if (c.estado === "parcial") return <span className="text-[10px] bg-amber-400/10 text-amber-400 px-1.5 py-0.5 rounded font-semibold">Pagado Parcial</span>;
  if (c.estado === "anulada_por_refinanciacion") return <span className="text-[10px] bg-violet-400/10 text-violet-400 px-1.5 py-0.5 rounded line-through">Anulado por Refinanciación</span>;
  if (esVencida || c.estado === "vencida") return <span className="text-[10px] bg-rose-400/10 text-rose-400 px-1.5 py-0.5 rounded font-semibold">Vencida</span>;
  return <span className="text-[10px] bg-white/5 text-slate-400 px-1.5 py-0.5 rounded">Pendiente</span>;
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function LoteadoraPage() {
  return (
    <LoteadoraErrorBoundary>
      <LoteadoraPageContent />
    </LoteadoraErrorBoundary>
  );
}

function LoteadoraPageContent() {
  const navigate = useNavigate();
  const { user, token, hasPermission, activeEmpresaPropia } = useContext(AuthContext);
  const headers = { Authorization: `Bearer ${token}` };
  const slug = activeEmpresaPropia?.slug || "";
  const eqs = slug ? `?empresa_id=${encodeURIComponent(slug)}` : "";
  const eqsAnd = slug ? `&empresa_id=${encodeURIComponent(slug)}` : "";
  const logoQs = slug ? `?logo_tipo=${encodeURIComponent(slug)}` : "";

  const [tab, setTab] = useState("panel");
  const [loteamientos, setLoteamientos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [stats, setStats] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const dataLoadedRef = useRef(false);
  const [loteamientoFiltro, setLoteamientoFiltro] = useState("");
  const [loteBusqueda, setLoteBusqueda] = useState(""); // busca por manzana, N°, CCC, finca madre, matrícula o padrón

  // Configuración del mapa
  const [mapaConfig, setMapaConfig] = useState(null);  // { lat_centro, lng_centro, zoom }
  const [configForm, setConfigForm] = useState({ lat_centro: "", lng_centro: "", zoom: "" });
  const [savingConfig, setSavingConfig] = useState(false);

  // Permisos
  const canVer              = hasPermission("loteadora.ver");
  const canGestionar        = hasPermission("loteadora.gestionar_lotes");
  const canReservar         = hasPermission("loteadora.reservar");
  const canGestionarVentas  = hasPermission("loteadora.gestionar_ventas");
  const canCobrar           = hasPermission("loteadora.cobrar_cuotas");
  const canVerCatastro      = hasPermission("loteadora.ver_catastro");
  const canConfigurar       = hasPermission("loteadora.configurar");

  // Modales
  const [modalLoteamiento, setModalLoteamiento] = useState(null);   // null | {} | doc
  const [modalLote, setModalLote] = useState(null);
  const [modalReserva, setModalReserva] = useState(null);           // null | lote
  const [modalVenta, setModalVenta] = useState(null);               // null | lote
  const [modalCuota, setModalCuota] = useState(null);               // null | {venta, cuota}
  const [modalVentaDetalle, setModalVentaDetalle] = useState(null); // null | venta
  const [modalLoteFicha, setModalLoteFicha] = useState(null);       // null | lote — ficha con trazabilidad
  const [modalTraspaso, setModalTraspaso] = useState(null);         // null | venta
  const [modalRefinanciar, setModalRefinanciar] = useState(null);   // null | venta
  const [saving, setSaving] = useState(false);

  // Forms
  const emptyLoteamiento = () => ({ nombre: "", descripcion: "", distrito: "", departamento: "", lat_centro: "", lng_centro: "", zoom: 16, geometria: [] });
  const emptyLote = () => ({ loteamiento_id: "", manzana: "", numero: "", tipo: "Estándar Urbano", precio_lista: "", entrega_minima: "", precio_contado_usd: "", entrega_minima_usd: "", precio_financiado_gs: "", precio_financiado_usd: "", superficie_m2: "", ccc: "", finca_madre: "", matricula: "", padron: "", linderos: "" });
  const emptyVenta = () => ({ cliente_id: "", cliente_nombre: "", cliente_ruc: "", tipo_venta: "cuotas", monto_total: "", n_cuotas: 12, tasa_interes_mensual: 0, tasa_punitoria_mensual: 0, fecha_venta: hoy(), cuenta_id: "", modo_seña: "primera_cuota", notas: "" });
  const emptyReserva = () => ({ cliente_id: "", cliente_nombre: "", cliente_ci: "", monto_seña: "", cuenta_id: "", fecha_vencimiento: "", notas: "" });
  const emptyTraspaso = () => ({
    cliente_destino_id: "", cliente_destino_nombre: "", cliente_destino_documento: "",
    motivo: "", fecha_traspaso: hoy(),
    recalcular: false, nueva_tasa_interes_mensual: "", nueva_fecha_inicio: hoy(), n_cuotas_restantes: "",
  });
  const [traspasoForm, setTraspasoForm] = useState(emptyTraspaso());
  const emptyRefinanciar = () => ({
    numeros_cuotas: [], tasa_interes_refinanciacion: "", n_cuotas_nuevas: "",
    fecha_inicio_nuevas: hoy(), motivo: "",
  });
  const [refinanciarForm, setRefinanciarForm] = useState(emptyRefinanciar());
  const [refinanciarPreview, setRefinanciarPreview] = useState([]);
  const [refinanciarPreviewLoading, setRefinanciarPreviewLoading] = useState(false);
  const emptyCuotaForm = () => ({
    monto_pagado: "", cuenta_id: "", fecha_pago: hoy(),
    aplicarDescuento: false, descuento_pct: "", descuento_monto: "",
    factura_numero: "", notas: "",
  });

  const [lotForm, setLotForm] = useState(emptyLoteamiento());
  const [loteForm, setLoteForm] = useState(emptyLote());
  const [ventaForm, setVentaForm] = useState(emptyVenta());
  const [reservaForm, setReservaForm] = useState(emptyReserva());
  const [cuotaForm, setCuotaForm] = useState(emptyCuotaForm());
  const [previewCuotas, setPreviewCuotas] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!canVer) { setLoading(false); return; }
    if (!dataLoadedRef.current) setLoading(true);
    else setRefreshing(true);
    try {
      const [rLot, rLotes, rVentas, rRes, rStats, rCuentas, rClientes] = await Promise.all([
        fetch(`${API}/admin/loteadora/loteamientos${eqs}`, { headers }),
        fetch(`${API}/admin/loteadora/lotes${eqs}`, { headers }),
        fetch(`${API}/admin/loteadora/ventas${eqs}`, { headers }),
        fetch(`${API}/admin/loteadora/reservas${eqs}`, { headers }),
        fetch(`${API}/admin/loteadora/stats${eqs}`, { headers }),
        fetch(`${API}/admin/cuentas-bancarias${logoQs}`, { headers }),
        fetch(`${API}/admin/entidades?es_cliente=true&activo=true${slug ? `&logo_tipo=${encodeURIComponent(slug)}` : ""}`, { headers }),
      ]);
      if (rLot.ok) setLoteamientos(asArray(await rLot.json()));
      if (rLotes.ok) setLotes(asArray(await rLotes.json()));
      if (rVentas.ok) setVentas(asArray(await rVentas.json()));
      if (rRes.ok) setReservas(asArray(await rRes.json()));
      if (rStats.ok) setStats(normalizarStats(await rStats.json()));
      if (rCuentas.ok) setCuentas(asArray(await rCuentas.json()));
      if (rClientes.ok) {
        const d = await rClientes.json();
        setClientes(Array.isArray(d) ? d : asArray(d.empresas));
      }
    } catch { }
    finally {
      dataLoadedRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, [canVer, eqs, logoQs]); // eslint-disable-line

  const upsertClienteLocal = (cliente) => {
    if (!cliente?.id) return;
    setClientes(prev => {
      const exists = prev.some(c => c.id === cliente.id);
      return exists ? prev.map(c => c.id === cliente.id ? cliente : c) : [cliente, ...prev];
    });
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useAutoRefresh(fetchAll, 30_000, !modalCuota && !modalVenta && !modalLote && !modalLoteamiento && !modalReserva);

  // ── Fetch configuración del mapa ─────────────────────────────────────────────
  const fetchMapaConfig = useCallback(async () => {
    if (!canVer || !slug) return;
    try {
      const r = await fetch(`${API}/admin/loteadora/config${eqs}`, { headers });
      if (r.ok) {
        const cfg = await r.json();
        setMapaConfig(cfg);
        setConfigForm({
          lat_centro: cfg.lat_centro != null ? String(cfg.lat_centro) : "",
          lng_centro: cfg.lng_centro != null ? String(cfg.lng_centro) : "",
          zoom:       cfg.zoom      != null ? String(cfg.zoom)       : "",
        });
      }
    } catch {}
  }, [canVer, slug, eqs]); // eslint-disable-line

  useEffect(() => { fetchMapaConfig(); }, [fetchMapaConfig]);
  useSSE({
    logoTipo: slug,
    token,
    enabled: !!slug && !!token,
    onEvent: useCallback((event) => {
      if (event.tipo === "modulo_actualizado" && event.data?.modulo === "loteadora") {
        fetchAll();
        fetchMapaConfig();
      }
    }, [fetchAll, fetchMapaConfig]),
  });

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      const body = {
        lat_centro: parseCoordenada(configForm.lat_centro, "latitud"),
        lng_centro: parseCoordenada(configForm.lng_centro, "longitud"),
        zoom:       configForm.zoom       !== "" ? Number(configForm.zoom)       : null,
      };
      const r = await fetch(`${API}/admin/loteadora/config${eqs}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await leerError(r));
      const cfg = await r.json();
      setMapaConfig(cfg);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(e.message); }
    finally { setSavingConfig(false); }
  };

  // ── Handlers Loteamiento ──────────────────────────────────────────────────────
  const handleSaveLoteamiento = async () => {
    if (!lotForm.nombre.trim()) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      const editing = modalLoteamiento?.id;
      const url = editing
        ? `${API}/admin/loteadora/loteamientos/${editing}${eqs}`
        : `${API}/admin/loteadora/loteamientos${eqs}`;
      const method = editing ? "PATCH" : "POST";
      const body = {
        ...lotForm,
        lat_centro: parseCoordenada(lotForm.lat_centro || centroGeometria(lotForm.geometria)?.lat, "latitud"),
        lng_centro: parseCoordenada(lotForm.lng_centro || centroGeometria(lotForm.geometria)?.lng, "longitud"),
        zoom: Number(lotForm.zoom) || 16,
        geometria: geometriaValida(lotForm.geometria),
      };
      const r = await fetch(url, { method, headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error(await leerError(r));
      toast.success(editing ? "Loteamiento actualizado" : "Loteamiento creado");
      setModalLoteamiento(null);
      fetchAll();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDeleteLoteamiento = async (id) => {
    if (!window.confirm("¿Eliminar este loteamiento?")) return;
    try {
      const r = await fetch(`${API}/admin/loteadora/loteamientos/${id}${eqs}`, { method: "DELETE", headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Loteamiento eliminado");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  // ── Handlers Lote ──────────────────────────────────────────────────────────
  const handleSaveLote = async () => {
    if (!loteForm.loteamiento_id) { toast.error("Seleccioná un loteamiento"); return; }
    if (!loteForm.manzana.trim() || !loteForm.numero.trim()) { toast.error("Manzana y número requeridos"); return; }
    if (!loteForm.precio_lista || Number(loteForm.precio_lista) <= 0) { toast.error("Precio inválido"); return; }
    setSaving(true);
    try {
      const editing = modalLote?.id;
      const url = editing
        ? `${API}/admin/loteadora/lotes/${editing}${eqs}`
        : `${API}/admin/loteadora/lotes${eqs}`;
      const method = editing ? "PATCH" : "POST";
      const body = {
        ...loteForm,
        precio_lista: Number(loteForm.precio_lista),
        entrega_minima: Number(loteForm.entrega_minima) || 0,
        precio_contado_usd: Number(loteForm.precio_contado_usd) || 0,
        entrega_minima_usd: Number(loteForm.entrega_minima_usd) || 0,
        precio_financiado_gs: Number(loteForm.precio_financiado_gs) || 0,
        precio_financiado_usd: Number(loteForm.precio_financiado_usd) || 0,
        superficie_m2: Number(loteForm.superficie_m2) || 0,
      };
      const r = await fetch(url, { method, headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success(editing ? "Lote actualizado" : "Lote registrado");
      setModalLote(null);
      fetchAll();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDeleteLote = async (id) => {
    if (!window.confirm("¿Eliminar este lote?")) return;
    try {
      const r = await fetch(`${API}/admin/loteadora/lotes/${id}${eqs}`, { method: "DELETE", headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Lote eliminado");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  // ── Handlers Reserva ──────────────────────────────────────────────────────
  const handleCrearReserva = async () => {
    const lote = modalReserva;
    if (!lote) return;
    if (!reservaForm.cliente_nombre.trim()) { toast.error("Nombre del cliente requerido"); return; }
    if (!reservaForm.monto_seña || Number(reservaForm.monto_seña) <= 0) { toast.error("Monto de seña inválido"); return; }
    if (!reservaForm.cuenta_id) { toast.error("Seleccioná una cuenta"); return; }
    if (!reservaForm.fecha_vencimiento) { toast.error("Fecha de vencimiento requerida"); return; }
    setSaving(true);
    try {
      const body = {
        lote_id: lote.id,
        cliente_id: reservaForm.cliente_id || "",
        cliente_nombre: reservaForm.cliente_nombre,
        cliente_ci: reservaForm.cliente_ci,
        monto_seña: Number(reservaForm.monto_seña),
        cuenta_id: reservaForm.cuenta_id,
        cuenta_nombre: cuentas.find(c => c.id === reservaForm.cuenta_id)?.nombre || "",
        fecha_vencimiento: reservaForm.fecha_vencimiento,
        notas: reservaForm.notas,
      };
      const r = await fetch(`${API}/admin/loteadora/reservas${eqs}`, { method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Reserva registrada");
      setModalReserva(null);
      setReservaForm(emptyReserva());
      fetchAll();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleLiberarReserva = async (id) => {
    if (!window.confirm("¿Liberar esta reserva? El lote volverá a estar disponible.")) return;
    try {
      const r = await fetch(`${API}/admin/loteadora/reservas/${id}/liberar${eqs}`, { method: "PATCH", headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Reserva liberada");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  // ── Preview cuotas ──────────────────────────────────────────────────────────
  const loadPreview = useCallback(async () => {
    const monto = Number(ventaForm.monto_total);
    const n = Number(ventaForm.n_cuotas);
    if (!monto || monto <= 0 || !n || n < 1 || ventaForm.tipo_venta !== "cuotas") { setPreviewCuotas([]); return; }
    setPreviewLoading(true);
    try {
      const params = new URLSearchParams({
        monto: monto,
        tasa: ventaForm.tasa_interes_mensual || 0,
        n_cuotas: n,
        fecha_inicio: ventaForm.fecha_venta || hoy(),
        ...(slug ? { empresa_id: slug } : {}),
      });
      const r = await fetch(`${API}/admin/loteadora/preview-cuotas?${params}`, { headers });
      if (r.ok) setPreviewCuotas(await r.json());
    } catch { }
    finally { setPreviewLoading(false); }
  }, [ventaForm.monto_total, ventaForm.n_cuotas, ventaForm.tasa_interes_mensual, ventaForm.fecha_venta, ventaForm.tipo_venta, slug]); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(loadPreview, 600);
    return () => clearTimeout(t);
  }, [loadPreview]);

  // ── Handler Venta ─────────────────────────────────────────────────────────
  const handleCrearVenta = async () => {
    const lote = modalVenta;
    if (!lote) return;
    if (!ventaForm.cliente_nombre.trim()) { toast.error("Nombre del cliente requerido"); return; }
    if (!ventaForm.monto_total || Number(ventaForm.monto_total) <= 0) { toast.error("Monto inválido"); return; }
    if (!ventaForm.cuenta_id) { toast.error("Seleccioná una cuenta"); return; }
    if (ventaForm.tipo_venta === "cuotas" && (!ventaForm.n_cuotas || Number(ventaForm.n_cuotas) < 1)) { toast.error("Número de cuotas inválido"); return; }
    setSaving(true);
    try {
      const body = {
        lote_id: lote.id,
        cliente_id: ventaForm.cliente_id || "",
        cliente_nombre: ventaForm.cliente_nombre,
        cliente_ruc: ventaForm.cliente_ruc,
        tipo_venta: ventaForm.tipo_venta,
        monto_total: Number(ventaForm.monto_total),
        n_cuotas: ventaForm.tipo_venta === "cuotas" ? Number(ventaForm.n_cuotas) : 1,
        tasa_interes_mensual: Number(ventaForm.tasa_interes_mensual) || 0,
        tasa_punitoria_mensual: Number(ventaForm.tasa_punitoria_mensual) || 0,
        fecha_venta: ventaForm.fecha_venta,
        cuenta_id: ventaForm.cuenta_id,
        cuenta_nombre: cuentas.find(c => c.id === ventaForm.cuenta_id)?.nombre || "",
        modo_seña: ventaForm.modo_seña,
        notas: ventaForm.notas,
      };
      const r = await fetch(`${API}/admin/loteadora/ventas${eqs}`, { method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Venta registrada");
      setModalVenta(null);
      setVentaForm(emptyVenta());
      setPreviewCuotas([]);
      fetchAll();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleCancelarVenta = async (id) => {
    if (!window.confirm("¿Cancelar esta venta? El lote volverá a estar disponible.")) return;
    try {
      const r = await fetch(`${API}/admin/loteadora/ventas/${id}/cancelar${eqs}`, { method: "PATCH", headers });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      toast.success("Venta cancelada");
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  // ── Handler Traspaso de Cuenta (cambio de titularidad) ────────────────────
  const abrirTraspaso = (venta) => {
    setTraspasoForm(emptyTraspaso());
    setModalTraspaso(venta);
  };

  const handleTraspaso = async () => {
    if (!modalTraspaso) return;
    if (!traspasoForm.cliente_destino_nombre.trim()) { toast.error("Nombre del cliente destino requerido"); return; }
    if (traspasoForm.recalcular && traspasoForm.n_cuotas_restantes && Number(traspasoForm.n_cuotas_restantes) < 1) {
      toast.error("Número de cuotas restantes inválido"); return;
    }
    setSaving(true);
    try {
      const body = {
        cliente_destino_id: traspasoForm.cliente_destino_id || "",
        cliente_destino_nombre: traspasoForm.cliente_destino_nombre,
        cliente_destino_documento: traspasoForm.cliente_destino_documento || "",
        motivo: traspasoForm.motivo || "",
        fecha_traspaso: traspasoForm.fecha_traspaso || hoy(),
        recalcular: !!traspasoForm.recalcular,
        nueva_tasa_interes_mensual: traspasoForm.recalcular && traspasoForm.nueva_tasa_interes_mensual !== ""
          ? Number(traspasoForm.nueva_tasa_interes_mensual) : null,
        nueva_fecha_inicio: traspasoForm.recalcular ? (traspasoForm.nueva_fecha_inicio || hoy()) : null,
        n_cuotas_restantes: traspasoForm.recalcular && traspasoForm.n_cuotas_restantes !== ""
          ? Number(traspasoForm.n_cuotas_restantes) : null,
      };
      const r = await fetch(`${API}/admin/loteadora/ventas/${modalTraspaso.id}/traspaso${eqs}`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      const data = await r.json();
      toast.success(data.mensaje || "Cuenta traspasada");
      setModalTraspaso(null);
      setModalVentaDetalle(null);
      setModalLoteFicha(null);
      fetchAll();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  // ── Handler Refinanciación de Deuda ────────────────────────────────────────
  const cuotasRefinanciables = (venta) =>
    (venta?.cuotas || []).filter(c => !["pagada", "parcial", "anulada_por_refinanciacion"].includes(c.estado));

  // Monto base según la regla estricta: vencidas completas, por vencer solo capital
  const montoBaseRefinanciado = (venta, numeros) => {
    const hoyStr = hoy();
    return (venta?.cuotas || [])
      .filter(c => numeros.includes(c.numero))
      .reduce((sum, c) => {
        const esVencida = (c.fecha_vencimiento || hoyStr) < hoyStr;
        const aporte = esVencida ? (c.monto_cuota || 0) : Math.max(0, (c.monto_cuota || 0) - (c.monto_interes || 0));
        return sum + aporte;
      }, 0);
  };

  const abrirRefinanciar = (venta) => {
    setRefinanciarForm(emptyRefinanciar());
    setRefinanciarPreview([]);
    setModalRefinanciar(venta);
  };

  const toggleCuotaRefinanciar = (numero) => {
    setRefinanciarForm(p => ({
      ...p,
      numeros_cuotas: p.numeros_cuotas.includes(numero)
        ? p.numeros_cuotas.filter(n => n !== numero)
        : [...p.numeros_cuotas, numero],
    }));
  };

  useEffect(() => {
    if (!modalRefinanciar) return;
    const monto = montoBaseRefinanciado(modalRefinanciar, refinanciarForm.numeros_cuotas);
    const n = Number(refinanciarForm.n_cuotas_nuevas);
    if (!monto || monto <= 0 || !n || n < 1) { setRefinanciarPreview([]); return; }
    setRefinanciarPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          monto: monto,
          tasa: refinanciarForm.tasa_interes_refinanciacion || 0,
          n_cuotas: n,
          fecha_inicio: refinanciarForm.fecha_inicio_nuevas || hoy(),
          ...(slug ? { empresa_id: slug } : {}),
        });
        const r = await fetch(`${API}/admin/loteadora/preview-cuotas?${params}`, { headers });
        if (r.ok) setRefinanciarPreview(await r.json());
      } catch { }
      finally { setRefinanciarPreviewLoading(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [modalRefinanciar, refinanciarForm.numeros_cuotas, refinanciarForm.tasa_interes_refinanciacion, refinanciarForm.n_cuotas_nuevas, refinanciarForm.fecha_inicio_nuevas]); // eslint-disable-line

  const handleRefinanciar = async () => {
    if (!modalRefinanciar) return;
    if (refinanciarForm.numeros_cuotas.length === 0) { toast.error("Seleccioná al menos una cuota a refinanciar"); return; }
    if (!refinanciarForm.n_cuotas_nuevas || Number(refinanciarForm.n_cuotas_nuevas) < 1) { toast.error("Ingresá el número de cuotas nuevas"); return; }
    if (refinanciarForm.tasa_interes_refinanciacion === "" || Number(refinanciarForm.tasa_interes_refinanciacion) < 0) {
      toast.error("Ingresá la tasa de interés de refinanciación (0 si no aplica interés)"); return;
    }
    setSaving(true);
    try {
      const body = {
        numeros_cuotas: refinanciarForm.numeros_cuotas,
        tasa_interes_refinanciacion: Number(refinanciarForm.tasa_interes_refinanciacion) || 0,
        n_cuotas_nuevas: Number(refinanciarForm.n_cuotas_nuevas),
        fecha_inicio_nuevas: refinanciarForm.fecha_inicio_nuevas || hoy(),
        motivo: refinanciarForm.motivo || "",
      };
      const r = await fetch(`${API}/admin/loteadora/ventas/${modalRefinanciar.id}/refinanciar${eqs}`, {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error"); }
      const data = await r.json();
      toast.success(data.mensaje || "Deuda refinanciada");
      setModalRefinanciar(null);
      setModalVentaDetalle(null);
      setModalLoteFicha(null);
      fetchAll();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  // ── Handler Cobrar Cuota (cobro parcial + descuento por pronto pago) ──────
  const [cuotaFacturando, setCuotaFacturando] = useState(false);

  const abrirCobrarCuota = (venta, cuota) => {
    setCuotaForm({ ...emptyCuotaForm(), monto_pagado: cuota.monto_cuota });
    setModalCuota({ venta, cuota });
  };

  const handleCobrarCuota = async () => {
    if (!modalCuota) return;
    const montoPagado = Number(cuotaForm.monto_pagado) || 0;
    if (montoPagado <= 0) { toast.error("Ingresá el monto pagado"); return; }
    if (!cuotaForm.cuenta_id) { toast.error("Seleccioná una cuenta bancaria"); return; }
    if (cuotaForm.aplicarDescuento && !cuotaForm.descuento_pct && !cuotaForm.descuento_monto) {
      toast.error("Ingresá el % o el monto del descuento"); return;
    }
    setCuotaFacturando(true);
    try {
      const body = {
        monto_pagado: montoPagado,
        fecha_pago: cuotaForm.fecha_pago || hoy(),
        cuenta_id: cuotaForm.cuenta_id,
        cuenta_nombre: cuentas.find(c => c.id === cuotaForm.cuenta_id)?.nombre || "",
        descuento_pct: cuotaForm.aplicarDescuento ? Number(cuotaForm.descuento_pct) || 0 : 0,
        descuento_monto: cuotaForm.aplicarDescuento && cuotaForm.descuento_monto ? Number(cuotaForm.descuento_monto) : null,
        factura_numero: cuotaForm.factura_numero || "",
        notas: cuotaForm.notas || "",
      };
      const r = await fetch(
        `${API}/admin/loteadora/ventas/${modalCuota.venta.id}/cuotas/${modalCuota.cuota.numero}/pagar${eqs}`,
        { method: "POST", headers: { ...headers, "Content-Type": "application/json" }, body: JSON.stringify(body) }
      );
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || "Error al registrar el cobro"); }
      const res = await r.json();
      toast.success(res.mensaje || "Cobro registrado");
      setModalCuota(null);
      fetchAll();
    } catch (e) { toast.error(e.message); }
    finally { setCuotaFacturando(false); }
  };

  // ── Tabs ─────────────────────────────────────────────────────────────────────
  const TABS = [
    { key: "panel",      label: "Panel",          icon: <LayoutGrid className="w-4 h-4" /> },
    { key: "cartera",    label: "Cartera",         icon: <Briefcase className="w-4 h-4" /> },
    { key: "reservas",   label: "Reservas",        icon: <Calendar className="w-4 h-4" /> },
    ...(canVerCatastro || canGestionar ? [{ key: "catastro",      label: "Catastro",      icon: <BookOpen className="w-4 h-4" /> }] : []),
    ...(canConfigurar                  ? [{ key: "configuracion", label: "Configuración", icon: <Settings2 className="w-4 h-4" /> }] : []),
  ];

  const lotesPorLoteamiento = loteamientoFiltro ? lotes.filter(l => l.loteamiento_id === loteamientoFiltro) : lotes;
  const lotesFiltrados = (() => {
    const q = loteBusqueda.trim().toLowerCase();
    if (!q) return lotesPorLoteamiento;
    return lotesPorLoteamiento.filter(l =>
      [l.manzana, l.numero, l.ccc, l.finca_madre, l.matricula, l.padron]
        .some(v => (v || "").toString().toLowerCase().includes(q))
    );
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  if (!canVer) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        Sin permiso para ver este módulo
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-arandu-dark text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white p-1 -ml-1 rounded-lg hover:bg-white/5 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <MapPin className="w-6 h-6 text-emerald-400" />
            <div>
              <h1 className="text-lg font-bold text-white">Loteadora</h1>
              <p className="text-xs text-slate-400">{activeEmpresaPropia?.nombre || ""} — Gestión de lotes y fraccionamientos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} disabled={refreshing} className={BTN_SEC}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <AuditoriaRegistroBtn modulo="loteadora" />
          </div>
        </div>

        {/* Stats rápidas */}
        {stats && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: "Total Lotes", value: stats.total, color: "text-slate-300" },
              { label: "Disponibles", value: stats.disponibles, color: "text-emerald-400" },
              { label: "Reservados", value: stats.reservados, color: "text-amber-400" },
              { label: "Vendidos", value: stats.vendidos, color: "text-rose-400" },
              { label: "Cuotas Vencidas", value: stats.cuotas_vencidas, color: stats.cuotas_vencidas > 0 ? "text-red-400" : "text-slate-400" },
              { label: "Saldo Pendiente", value: fmtGs(stats.saldo_pendiente), color: "text-blue-400" },
            ].map(s => (
              <div key={s.label} className="bg-white/5 rounded-xl border border-white/10 px-3 py-2 text-center">
                <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-4 border-b border-white/10 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key
                ? "text-white border-blue-500 bg-blue-500/10"
                : "text-slate-400 border-transparent hover:text-white hover:border-white/20"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* ── TAB: PANEL ─────────────────────────────────────────────────── */}
        {tab === "panel" && (
          <div className="space-y-6">
            {/* Filtro loteamiento */}
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={loteamientoFiltro}
                onChange={e => setLoteamientoFiltro(e.target.value)}
                className="bg-arandu-dark-light border border-white/10 text-sm text-white rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
              >
                <option value="">Todos los loteamientos</option>
                {loteamientos.map(l => (
                  <option key={l.id} value={l.id}>{l.nombre}</option>
                ))}
              </select>
              <input
                value={loteBusqueda}
                onChange={e => setLoteBusqueda(e.target.value)}
                placeholder="Buscar por Mza, N°, CCC, Finca Madre, Matrícula o Padrón..."
                className="bg-arandu-dark-light border border-white/10 text-sm text-white rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 w-72 max-w-full"
              />
              {canGestionar && (
                <button
                  onClick={() => { setLotForm(emptyLoteamiento()); setModalLoteamiento({}); }}
                  className={BTN_PRI}
                >
                  <Plus className="w-4 h-4" /> Nuevo Loteamiento
                </button>
              )}
              {canGestionar && (
                <button
                  onClick={() => { setLoteForm({ ...emptyLote(), loteamiento_id: loteamientoFiltro || (loteamientos[0]?.id || "") }); setModalLote({}); }}
                  className={BTN_SEC}
                >
                  <Plus className="w-4 h-4" /> Nuevo Lote
                </button>
              )}
            </div>

            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={() => {
                  const lt = loteamientos.find(l => l.id === loteamientoFiltro);
                  const geo = centroGeometria(lt?.geometria);
                  const lat = geo?.lat ?? lt?.lat_centro ?? mapaConfig?.lat_centro;
                  const lng = geo?.lng ?? lt?.lng_centro ?? mapaConfig?.lng_centro;
                  abrirStructec({ lat, lng, zoom: lt?.zoom || mapaConfig?.zoom || 16 });
                }}
                className={BTN_SEC}
                title="Abre Structec Mapas (catastro, hidrografía, imágenes aéreas) en una pestaña nueva"
              >
                <MapPin className="w-4 h-4" /> Ver en Structec Mapas
              </button>
            </div>
            {/* Mapa Leaflet */}
            <MapaLotes
              lotes={lotesFiltrados}
              loteamientos={loteamientos}
              loteamientoActivo={loteamientoFiltro}
              defaultCenter={mapaConfig?.lat_centro != null ? { lat: mapaConfig.lat_centro, lng: mapaConfig.lng_centro, zoom: mapaConfig.zoom } : null}
              onLoteClick={lote => {
                if (lote.estado === "disponible" && canGestionarVentas) {
                  setVentaForm({ ...emptyVenta(), monto_total: lote.precio_lista });
                  setPreviewCuotas([]);
                  setModalVenta(lote);
                } else if (lote.estado === "disponible" && canReservar) {
                  setReservaForm(emptyReserva());
                  setModalReserva(lote);
                }
              }}
            />

            {/* Leyenda */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-400 inline-block" />Disponible</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" />Reservado</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-rose-400 inline-block" />Vendido</span>
            </div>

            {/* Grid de lotes */}
            <div>
              <h3 className="text-sm font-medium text-slate-300 mb-3">
                Lotes {loteamientoFiltro ? `— ${loteamientos.find(l => l.id === loteamientoFiltro)?.nombre}` : ""}
                <span className="ml-2 text-slate-500 font-normal">({lotesFiltrados.length})</span>
              </h3>
              {lotesFiltrados.length === 0 ? (
                <div className="text-center py-10 text-slate-500">
                  <MapPin className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No hay lotes registrados</p>
                  {canGestionar && (
                    <button
                      onClick={() => { setLoteForm(emptyLote()); setModalLote({}); }}
                      className="mt-3 text-blue-400 text-sm hover:underline"
                    >
                      + Registrar primer lote
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {lotesFiltrados.map(lote => (
                    <LoteCard
                      key={lote.id}
                      lote={lote}
                      canGestionar={canGestionar}
                      canReservar={canReservar}
                      canGestionarVentas={canGestionarVentas}
                      onEdit={() => {
                        setLoteForm({
                          loteamiento_id: lote.loteamiento_id,
                          manzana: lote.manzana,
                          numero: lote.numero,
                          tipo: lote.tipo,
                          precio_lista: lote.precio_lista,
                          entrega_minima: lote.entrega_minima || "",
                          precio_contado_usd: lote.precio_contado_usd || "",
                          entrega_minima_usd: lote.entrega_minima_usd || "",
                          precio_financiado_gs: lote.precio_financiado_gs || "",
                          precio_financiado_usd: lote.precio_financiado_usd || "",
                          superficie_m2: lote.superficie_m2 || "",
                          ccc: lote.ccc || "",
                          finca_madre: lote.finca_madre || "",
                          matricula: lote.matricula || "",
                          padron: lote.padron || "",
                          linderos: lote.linderos || "",
                        });
                        setModalLote(lote);
                      }}
                      onDelete={() => handleDeleteLote(lote.id)}
                      onFicha={() => setModalLoteFicha(lote)}
                      onReservar={() => { setReservaForm(emptyReserva()); setModalReserva(lote); }}
                      onVender={() => {
                        setVentaForm({ ...emptyVenta(), monto_total: lote.precio_lista });
                        setPreviewCuotas([]);
                        setModalVenta(lote);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB: CARTERA ────────────────────────────────────────────────── */}
        {tab === "cartera" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-300">Ventas y cartera de cuotas</h3>
              <span className="text-xs text-slate-500">{ventas.length} ventas</span>
            </div>
            {ventas.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Briefcase className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No hay ventas registradas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ventas.map(v => (
                  <VentaRow
                    key={v.id}
                    venta={v}
                    canCobrar={canCobrar}
                    canGestionarVentas={canGestionarVentas}
                    onCobrar={(cuota) => abrirCobrarCuota(v, cuota)}
                    onCancelar={() => handleCancelarVenta(v.id)}
                    onDetalle={() => setModalVentaDetalle(v)}
                    onTraspaso={() => abrirTraspaso(v)}
                    onRefinanciar={() => abrirRefinanciar(v)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB: RESERVAS ────────────────────────────────────────────────── */}
        {tab === "reservas" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-300">Reservas activas</h3>
              <span className="text-xs text-slate-500">{reservas.length} activas</span>
            </div>
            {reservas.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No hay reservas activas</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-400 uppercase tracking-wider bg-white/5">
                    <tr>
                      <th className="text-left px-4 py-3">Lote</th>
                      <th className="text-left px-4 py-3">Cliente</th>
                      <th className="text-left px-4 py-3">Seña</th>
                      <th className="text-left px-4 py-3">Vence</th>
                      <th className="text-left px-4 py-3">Cuenta</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {reservas.map(r => {
                      const vencida = r.fecha_vencimiento < hoy();
                      return (
                        <tr key={r.id} className="hover:bg-white/5">
                          <td className="px-4 py-3 text-white font-medium">Mza {r.lote_manzana} - N°{r.lote_numero}<br /><span className="text-[10px] text-slate-400">{r.loteamiento_nombre}</span></td>
                          <td className="px-4 py-3 text-slate-200">{r.cliente_nombre}</td>
                          <td className="px-4 py-3 text-amber-300 font-medium">{fmtGs(r.monto_seña)}</td>
                          <td className={`px-4 py-3 text-sm ${vencida ? "text-rose-400 font-medium" : "text-slate-300"}`}>{r.fecha_vencimiento}{vencida && " ⚠"}</td>
                          <td className="px-4 py-3 text-xs text-slate-400">{r.cuenta_nombre}</td>
                          <td className="px-4 py-3 text-right">
                            {canReservar && (
                              <button onClick={() => handleLiberarReserva(r.id)} className={BTN_DNG}>
                                Liberar
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: CATASTRO ────────────────────────────────────────────────── */}
        {tab === "catastro" && (
          <div className="space-y-6">
            {/* Loteamientos */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-300">Loteamientos</h3>
                {canGestionar && (
                  <button onClick={() => { setLotForm(emptyLoteamiento()); setModalLoteamiento({}); }} className={BTN_PRI}>
                    <Plus className="w-4 h-4" /> Nuevo
                  </button>
                )}
              </div>
              {loteamientos.length === 0 ? (
                <p className="text-slate-500 text-sm">Sin loteamientos registrados</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-400 uppercase tracking-wider bg-white/5">
                      <tr>
                        <th className="text-left px-4 py-3">Nombre</th>
                        <th className="text-left px-4 py-3">Distrito</th>
                        <th className="text-left px-4 py-3">Departamento</th>
                        <th className="text-left px-4 py-3">Lotes</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {loteamientos.map(l => (
                        <tr key={l.id} className="hover:bg-white/5">
                          <td className="px-4 py-3 font-medium text-white">{l.nombre}</td>
                          <td className="px-4 py-3 text-slate-300">{l.distrito || "—"}</td>
                          <td className="px-4 py-3 text-slate-300">{l.departamento || "—"}</td>
                          <td className="px-4 py-3 text-slate-400">{lotes.filter(x => x.loteamiento_id === l.id).length}</td>
                          <td className="px-4 py-3 text-right">
                            {canGestionar && (
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={() => { setLotForm({ nombre: l.nombre, descripcion: l.descripcion || "", distrito: l.distrito || "", departamento: l.departamento || "", lat_centro: l.lat_centro || "", lng_centro: l.lng_centro || "", zoom: l.zoom || 16, geometria: geometriaValida(l.geometria) }); setModalLoteamiento(l); }}
                                  className="text-blue-400 hover:text-blue-300 p-1"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteLoteamiento(l.id)} className="text-rose-400 hover:text-rose-300 p-1">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Lotes */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-300">Lotes registrados</h3>
                {canGestionar && (
                  <button onClick={() => { setLoteForm(emptyLote()); setModalLote({}); }} className={BTN_PRI}>
                    <Plus className="w-4 h-4" /> Nuevo Lote
                  </button>
                )}
              </div>
              {lotes.length === 0 ? (
                <p className="text-slate-500 text-sm">Sin lotes registrados</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-400 uppercase tracking-wider bg-white/5">
                      <tr>
                        <th className="text-left px-4 py-3">Loteamiento</th>
                        <th className="text-left px-4 py-3">Mza</th>
                        <th className="text-left px-4 py-3">N°</th>
                        <th className="text-left px-4 py-3">Tipo</th>
                        <th className="text-left px-4 py-3">Precio</th>
                        <th className="text-left px-4 py-3">m²</th>
                        <th className="text-left px-4 py-3">CCC</th>
                        <th className="text-left px-4 py-3">Estado</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {lotes.map(l => (
                        <tr key={l.id} className="hover:bg-white/5">
                          <td className="px-4 py-3 text-slate-400 text-xs">{l.loteamiento_nombre}</td>
                          <td className="px-4 py-3 font-medium text-white">{l.manzana}</td>
                          <td className="px-4 py-3 text-white">{l.numero}</td>
                          <td className="px-4 py-3 text-slate-300 text-xs">{l.tipo}</td>
                          <td className="px-4 py-3 text-emerald-300 font-medium">{fmtGs(l.precio_lista)}</td>
                          <td className="px-4 py-3 text-slate-400">{l.superficie_m2 ? `${l.superficie_m2} m²` : "—"}</td>
                          <td className="px-4 py-3 text-slate-400 text-xs font-mono">{l.ccc || "—"}</td>
                          <td className="px-4 py-3"><EstadoBadge estado={l.estado} /></td>
                          <td className="px-4 py-3 text-right">
                            {canGestionar && l.estado === "disponible" && (
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={() => {
                                    setLoteForm({ loteamiento_id: l.loteamiento_id, manzana: l.manzana, numero: l.numero, tipo: l.tipo, precio_lista: l.precio_lista, entrega_minima: l.entrega_minima || "", precio_contado_usd: l.precio_contado_usd || "", entrega_minima_usd: l.entrega_minima_usd || "", precio_financiado_gs: l.precio_financiado_gs || "", precio_financiado_usd: l.precio_financiado_usd || "", superficie_m2: l.superficie_m2 || "", ccc: l.ccc || "", finca_madre: l.finca_madre || "", linderos: l.linderos || "" });
                                    setModalLote(l);
                                  }}
                                  className="text-blue-400 hover:text-blue-300 p-1"
                                ><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => handleDeleteLote(l.id)} className="text-rose-400 hover:text-rose-300 p-1"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── TAB: CONFIGURACIÓN ───────────────────────────────────────────── */}
        {tab === "configuracion" && (
          <div className="space-y-4 max-w-lg">
            <p className="text-slate-400 text-sm">Configuración del módulo Loteadora.</p>

            {/* Card: Centro del mapa */}
            <div className="border border-white/10 rounded-xl p-5 bg-white/3 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Punto de inicio del mapa</p>
                  <p className="text-slate-400 text-xs mt-0.5">
                    Definí la posición y el zoom con que se abre el mapa global de lotes. Si no se configura, el mapa arranca en Asunción.
                  </p>
                </div>
              </div>

              {mapaConfig?.lat_centro != null && (
                <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-400">
                  Actual: <span className="text-white font-mono">{mapaConfig.lat_centro}, {mapaConfig.lng_centro}</span>
                  &nbsp;— zoom <span className="text-white font-mono">{mapaConfig.zoom ?? "—"}</span>
                </div>
              )}

              <Row>
                <Field label="Latitud" half>
                  <input
                    inputMode="decimal" className={INPUT}
                    value={configForm.lat_centro}
                    onChange={e => setConfigForm(p => ({ ...p, lat_centro: e.target.value }))}
                    placeholder="-25.2867"
                  />
                </Field>
                <Field label="Longitud" half>
                  <input
                    inputMode="decimal" className={INPUT}
                    value={configForm.lng_centro}
                    onChange={e => setConfigForm(p => ({ ...p, lng_centro: e.target.value }))}
                    placeholder="-57.6470"
                  />
                </Field>
              </Row>
              <Field label="Zoom (1–18)">
                <input
                  type="number" min={1} max={18} className={INPUT}
                  value={configForm.zoom}
                  onChange={e => setConfigForm(p => ({ ...p, zoom: e.target.value }))}
                  placeholder="13"
                />
              </Field>

              <div className="flex justify-end pt-1">
                <button onClick={handleSaveConfig} disabled={savingConfig} className={BTN_PRI}>
                  {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Guardar</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── MODAL: Loteamiento ───────────────────────────────────────────────── */}
      {modalLoteamiento !== null && (
        <Modal title={modalLoteamiento?.id ? "Editar Loteamiento" : "Nuevo Loteamiento"} icon={<MapPin className="w-5 h-5 text-emerald-400" />} onClose={() => setModalLoteamiento(null)} wide>
          <div className="space-y-4">
            <Field label="Nombre *">
              <input className={INPUT} value={lotForm.nombre} onChange={e => setLotForm(p => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Residencial San Lorenzo" />
            </Field>
            <Field label="Descripción">
              <textarea className={INPUT} rows={2} value={lotForm.descripcion} onChange={e => setLotForm(p => ({ ...p, descripcion: e.target.value }))} placeholder="Descripción opcional" />
            </Field>
            <Row>
              <Field label="Distrito" half><input className={INPUT} value={lotForm.distrito} onChange={e => setLotForm(p => ({ ...p, distrito: e.target.value }))} placeholder="Ej: San Lorenzo" /></Field>
              <Field label="Departamento" half><input className={INPUT} value={lotForm.departamento} onChange={e => setLotForm(p => ({ ...p, departamento: e.target.value }))} placeholder="Ej: Central" /></Field>
            </Row>
            <p className="text-xs text-slate-500">Centro del mapa (opcional — para centrar la vista en el mapa)</p>
            <Row>
              <Field label="Latitud" half><input inputMode="decimal" className={INPUT} value={lotForm.lat_centro} onChange={e => setLotForm(p => ({ ...p, lat_centro: e.target.value }))} placeholder="-25.28" /></Field>
              <Field label="Longitud" half><input inputMode="decimal" className={INPUT} value={lotForm.lng_centro} onChange={e => setLotForm(p => ({ ...p, lng_centro: e.target.value }))} placeholder="-57.64" /></Field>
            </Row>
            <Field label="Área del loteamiento">
              <EditorAreaLoteamiento
                value={lotForm.geometria}
                center={{
                  lat: lotForm.lat_centro !== "" ? Number(lotForm.lat_centro) : mapaConfig?.lat_centro,
                  lng: lotForm.lng_centro !== "" ? Number(lotForm.lng_centro) : mapaConfig?.lng_centro,
                }}
                zoom={Number(lotForm.zoom) || 16}
                onChange={geo => {
                  const centro = centroGeometria(geo);
                  setLotForm(p => ({
                    ...p,
                    geometria: geo,
                    lat_centro: centro ? String(centro.lat) : p.lat_centro,
                    lng_centro: centro ? String(centro.lng) : p.lng_centro,
                  }));
                }}
              />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalLoteamiento(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleSaveLoteamiento} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Guardar</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL: Lote ─────────────────────────────────────────────────────── */}
      {modalLote !== null && (
        <Modal title={modalLote?.id ? "Editar Lote" : "Nuevo Lote"} icon={<Home className="w-5 h-5 text-blue-400" />} onClose={() => setModalLote(null)} wide>
          <div className="space-y-4">
            <Field label="Loteamiento *">
              <select className={INPUT} value={loteForm.loteamiento_id} onChange={e => setLoteForm(p => ({ ...p, loteamiento_id: e.target.value }))}>
                <option value="">— Seleccioná —</option>
                {loteamientos.map(l => <option key={l.id} value={l.id}>{l.nombre}</option>)}
              </select>
            </Field>
            <Row>
              <Field label="Manzana *" half><input className={INPUT} value={loteForm.manzana} onChange={e => setLoteForm(p => ({ ...p, manzana: e.target.value }))} placeholder="A" /></Field>
              <Field label="N° Lote *" half><input className={INPUT} value={loteForm.numero} onChange={e => setLoteForm(p => ({ ...p, numero: e.target.value }))} placeholder="01" /></Field>
            </Row>
            <Row>
              <Field label="Tipo" half>
                <select className={INPUT} value={loteForm.tipo} onChange={e => setLoteForm(p => ({ ...p, tipo: e.target.value }))}>
                  {["Estándar Urbano", "Comercial", "Industrial", "Residencial Premium", "Esquina", "Otro"].map(t => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Superficie (m²)" half><input type="number" className={INPUT} value={loteForm.superficie_m2} onChange={e => setLoteForm(p => ({ ...p, superficie_m2: e.target.value }))} placeholder="360" /></Field>
            </Row>
            <Row>
              <Field label="Precio Contado (Gs.) *" half><input type="number" className={INPUT} value={loteForm.precio_lista} onChange={e => setLoteForm(p => ({ ...p, precio_lista: e.target.value }))} placeholder="35000000" /></Field>
              <Field label="Entrega mínima (Gs.)" half><input type="number" className={INPUT} value={loteForm.entrega_minima} onChange={e => setLoteForm(p => ({ ...p, entrega_minima: e.target.value }))} placeholder="5000000" /></Field>
            </Row>
            <Row>
              <Field label="Precio Contado (USD)" half><input type="number" className={INPUT} value={loteForm.precio_contado_usd} onChange={e => setLoteForm(p => ({ ...p, precio_contado_usd: e.target.value }))} placeholder="5000" /></Field>
              <Field label="Entrega mínima (USD)" half><input type="number" className={INPUT} value={loteForm.entrega_minima_usd} onChange={e => setLoteForm(p => ({ ...p, entrega_minima_usd: e.target.value }))} placeholder="700" /></Field>
            </Row>
            <Row>
              <Field label="Precio Financiado (USD)" half><input type="number" className={INPUT} value={loteForm.precio_financiado_usd} onChange={e => setLoteForm(p => ({ ...p, precio_financiado_usd: e.target.value }))} placeholder="5500" /></Field>
              <Field label="Precio Financiado (Gs.)" half><input type="number" className={INPUT} value={loteForm.precio_financiado_gs} onChange={e => setLoteForm(p => ({ ...p, precio_financiado_gs: e.target.value }))} placeholder="38500000" /></Field>
            </Row>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider pt-2">Datos catastrales SNC (opcional)</p>
            <Row>
              <Field label="CCC" half><input className={INPUT} value={loteForm.ccc} onChange={e => setLoteForm(p => ({ ...p, ccc: e.target.value }))} placeholder="24-0105-01" /></Field>
              <Field label="Finca Madre" half><input className={INPUT} value={loteForm.finca_madre} onChange={e => setLoteForm(p => ({ ...p, finca_madre: e.target.value }))} placeholder="1234" /></Field>
            </Row>
            <Row>
              <Field label="Matrícula" half><input className={INPUT} value={loteForm.matricula} onChange={e => setLoteForm(p => ({ ...p, matricula: e.target.value }))} placeholder="M-12345" /></Field>
              <Field label="Padrón" half><input className={INPUT} value={loteForm.padron} onChange={e => setLoteForm(p => ({ ...p, padron: e.target.value }))} placeholder="P-6789" /></Field>
            </Row>
            <Field label="Linderos">
              <textarea className={INPUT} rows={2} value={loteForm.linderos} onChange={e => setLoteForm(p => ({ ...p, linderos: e.target.value }))} placeholder="N: Av. España — S: Lote 02 — E: Mza B — O: Ruta 1" />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalLote(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleSaveLote} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Guardar</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL: Reserva ───────────────────────────────────────────────────── */}
      {modalReserva && (
        <Modal title={`Reservar — Mza ${modalReserva.manzana} Lote ${modalReserva.numero}`} icon={<Calendar className="w-5 h-5 text-amber-400" />} onClose={() => setModalReserva(null)}>
          <div className="space-y-4">
            <div className="bg-amber-400/10 border border-amber-400/20 rounded-xl px-4 py-3 text-sm text-amber-300">
              {modalReserva.loteamiento_nombre} · {fmtGs(modalReserva.precio_lista)}
            </div>
            <ClienteLoteadoraSelect
              clientes={clientes}
              token={token}
              logo={slug}
              onClienteCreado={upsertClienteLocal}
              value={{
                id: reservaForm.cliente_id,
                nombre: reservaForm.cliente_nombre,
                ruc: reservaForm.cliente_ci,
              }}
              documentoLabel="CI / RUC"
              onChange={cliente => setReservaForm(p => ({
                ...p,
                cliente_id: cliente.id || "",
                cliente_nombre: cliente.nombre || "",
                cliente_ci: cliente.ruc || "",
              }))}
            />
            <Row>
              <Field label="Monto seña (Gs.) *" half><input type="number" className={INPUT} value={reservaForm.monto_seña} onChange={e => setReservaForm(p => ({ ...p, monto_seña: e.target.value }))} /></Field>
              <Field label="Válida hasta *" half><DateInput className={INPUT} value={reservaForm.fecha_vencimiento} onChange={e => setReservaForm(p => ({ ...p, fecha_vencimiento: e.target.value }))} /></Field>
            </Row>
            <Field label="Cuenta bancaria *">
              <select className={INPUT} value={reservaForm.cuenta_id} onChange={e => setReservaForm(p => ({ ...p, cuenta_id: e.target.value }))}>
                <option value="">— Seleccioná —</option>
                <CuentaOptions cuentas={cuentas} />
              </select>
            </Field>
            <Field label="Notas">
              <textarea className={INPUT} rows={2} value={reservaForm.notas} onChange={e => setReservaForm(p => ({ ...p, notas: e.target.value }))} />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalReserva(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleCrearReserva} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Registrar Reserva</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL: Venta ─────────────────────────────────────────────────────── */}
      {modalVenta && (
        <Modal title={`Venta — Mza ${modalVenta.manzana} Lote ${modalVenta.numero}`} icon={<DollarSign className="w-5 h-5 text-emerald-400" />} onClose={() => { setModalVenta(null); setPreviewCuotas([]); }} wide>
          <div className="space-y-4">
            <div className="bg-emerald-400/10 border border-emerald-400/20 rounded-xl px-4 py-3 text-sm text-emerald-300">
              {modalVenta.loteamiento_nombre} · Precio lista: {fmtGs(modalVenta.precio_lista)}
            </div>
            <ClienteLoteadoraSelect
              clientes={clientes}
              token={token}
              logo={slug}
              onClienteCreado={upsertClienteLocal}
              value={{
                id: ventaForm.cliente_id,
                nombre: ventaForm.cliente_nombre,
                ruc: ventaForm.cliente_ruc,
              }}
              onChange={cliente => setVentaForm(p => ({
                ...p,
                cliente_id: cliente.id || "",
                cliente_nombre: cliente.nombre || "",
                cliente_ruc: cliente.ruc || "",
              }))}
            />
            <Row>
              <Field label="Tipo de venta *" half>
                <select className={INPUT} value={ventaForm.tipo_venta} onChange={e => setVentaForm(p => ({ ...p, tipo_venta: e.target.value }))}>
                  <option value="cuotas">A cuotas</option>
                  <option value="contado">Al contado</option>
                </select>
              </Field>
              <Field label="Fecha de venta *" half><DateInput className={INPUT} value={ventaForm.fecha_venta} onChange={e => setVentaForm(p => ({ ...p, fecha_venta: e.target.value }))} /></Field>
            </Row>
            <Row>
              <Field label="Monto total (Gs.) *" half><input type="number" className={INPUT} value={ventaForm.monto_total} onChange={e => setVentaForm(p => ({ ...p, monto_total: e.target.value }))} /></Field>
              {ventaForm.tipo_venta === "cuotas" && (
                <Field label="N° Cuotas" half><input type="number" min={1} max={360} className={INPUT} value={ventaForm.n_cuotas} onChange={e => setVentaForm(p => ({ ...p, n_cuotas: e.target.value }))} /></Field>
              )}
            </Row>
            {ventaForm.tipo_venta === "cuotas" && (
              <Row>
                <Field label="Tasa mensual % (0 = sin interés)" half>
                  <input type="number" step="0.1" min={0} className={INPUT} value={ventaForm.tasa_interes_mensual} onChange={e => setVentaForm(p => ({ ...p, tasa_interes_mensual: e.target.value }))} placeholder="0.0" />
                </Field>
                <Field label="Tasa punitoria mensual % (mora)" half>
                  <input type="number" step="0.1" min={0} className={INPUT} value={ventaForm.tasa_punitoria_mensual} onChange={e => setVentaForm(p => ({ ...p, tasa_punitoria_mensual: e.target.value }))} placeholder="0.0" />
                </Field>
              </Row>
            )}
            <Field label="Aplicación de seña (si hay reserva)">
              <select className={INPUT} value={ventaForm.modo_seña} onChange={e => setVentaForm(p => ({ ...p, modo_seña: e.target.value }))}>
                <option value="primera_cuota">Cubre la primera cuota</option>
                <option value="reducir_total">Reduce el monto financiado</option>
                <option value="deposito_separado">Depósito separado (no afecta cuotas)</option>
              </select>
            </Field>
            <Field label="Cuenta bancaria *">
              <select className={INPUT} value={ventaForm.cuenta_id} onChange={e => setVentaForm(p => ({ ...p, cuenta_id: e.target.value }))}>
                <option value="">— Seleccioná —</option>
                <CuentaOptions cuentas={cuentas} />
              </select>
            </Field>
            <Field label="Notas">
              <textarea className={INPUT} rows={2} value={ventaForm.notas} onChange={e => setVentaForm(p => ({ ...p, notas: e.target.value }))} />
            </Field>

            {/* Preview cuotas */}
            {ventaForm.tipo_venta === "cuotas" && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Preview cuotas</p>
                {previewLoading ? (
                  <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Calculando...</div>
                ) : previewCuotas.length > 0 ? (
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-white/10">
                    <table className="w-full text-xs">
                      <thead className="bg-white/5 text-slate-400">
                        <tr>
                          <th className="text-left px-3 py-2">N°</th>
                          <th className="text-left px-3 py-2">Vence</th>
                          <th className="text-right px-3 py-2">Capital</th>
                          <th className="text-right px-3 py-2">Interés</th>
                          <th className="text-right px-3 py-2">Cuota</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {previewCuotas.map(c => (
                          <tr key={c.numero} className="hover:bg-white/5">
                            <td className="px-3 py-1.5 text-slate-300">{c.numero}</td>
                            <td className="px-3 py-1.5 text-slate-400">{c.fecha_vencimiento}</td>
                            <td className="px-3 py-1.5 text-right text-slate-300">{fmtNum(c.monto_capital)}</td>
                            <td className="px-3 py-1.5 text-right text-slate-400">{fmtNum(c.monto_interes)}</td>
                            <td className="px-3 py-1.5 text-right text-emerald-300 font-medium">{fmtNum(c.monto_cuota)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setModalVenta(null); setPreviewCuotas([]); }} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleCrearVenta} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Registrar Venta</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL: Cobrar Cuota (parcial + descuento por pronto pago) ────────── */}
      {modalCuota && (() => {
        const cuota = modalCuota.cuota;
        const montoPagado = Number(cuotaForm.monto_pagado) || 0;
        const esAnticipado = (cuotaForm.fecha_pago || hoy()) < cuota.fecha_vencimiento;
        const descuentoMonto = cuotaForm.aplicarDescuento
          ? (cuotaForm.descuento_monto ? Number(cuotaForm.descuento_monto) : Math.round(cuota.monto_cuota * ((Number(cuotaForm.descuento_pct) || 0) / 100)))
          : 0;
        const montoExigido = Math.max(0, cuota.monto_cuota - descuentoMonto);
        const saldoResultante = Math.max(0, montoExigido - montoPagado);
        const seraParcial = !cuotaForm.aplicarDescuento && saldoResultante > 1;

        return (
        <Modal
          title={`Cobrar Cuota N°${cuota.numero}/${modalCuota.venta.n_cuotas}`}
          icon={<FileText className="w-5 h-5 text-blue-400" />}
          onClose={() => setModalCuota(null)}
          wide
        >
          <div className="space-y-4">
            {/* Info cuota */}
            <div className="bg-white/5 rounded-xl px-4 py-3 text-sm space-y-1">
              <p className="font-medium text-white">{modalCuota.venta.loteamiento_nombre} — Mza {modalCuota.venta.lote_manzana} Lote {modalCuota.venta.lote_numero}</p>
              <p className="text-slate-400">Cliente: {modalCuota.venta.cliente_nombre}</p>
              <p className="text-slate-400">Vencimiento: {cuota.fecha_vencimiento}</p>
              <p className="text-emerald-300 font-semibold">Monto cuota: {fmtGs(cuota.monto_cuota)}</p>
              {cuota.monto_interes > 0 && (
                <p className="text-xs text-slate-500">Capital: {fmtNum(cuota.monto_capital)} · Interés: {fmtNum(cuota.monto_interes)}</p>
              )}
              {(cuota.saldo_anterior_arrastrado > 0 || cuota.interes_punitorio_generado > 0) && (
                <p className="text-xs text-amber-400">
                  Incluye saldo arrastrado de cuota anterior: {fmtNum(cuota.saldo_anterior_arrastrado)}
                  {cuota.interes_punitorio_generado > 0 && ` + interés punitorio: ${fmtNum(cuota.interes_punitorio_generado)}`}
                </p>
              )}
            </div>

            <Row>
              <Field label="Monto pagado (Gs.) *" half>
                <input type="number" className={INPUT} value={cuotaForm.monto_pagado} onChange={e => setCuotaForm(p => ({ ...p, monto_pagado: e.target.value }))} />
              </Field>
              <Field label="Fecha de pago" half>
                <DateInput className={INPUT} value={cuotaForm.fecha_pago} onChange={e => setCuotaForm(p => ({ ...p, fecha_pago: e.target.value }))} />
              </Field>
            </Row>
            <Field label="Cuenta bancaria *">
              <select className={INPUT} value={cuotaForm.cuenta_id} onChange={e => setCuotaForm(p => ({ ...p, cuenta_id: e.target.value }))}>
                <option value="">— Seleccioná —</option>
                <CuentaOptions cuentas={cuentas} />
              </select>
            </Field>

            {/* Escenario B: descuento por pago anticipado */}
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${esAnticipado ? "text-slate-300" : "text-slate-600"}`}>
              <input
                type="checkbox"
                checked={cuotaForm.aplicarDescuento}
                disabled={!esAnticipado}
                onChange={e => setCuotaForm(p => ({ ...p, aplicarDescuento: e.target.checked }))}
              />
              Aplicar descuento por pago anticipado {!esAnticipado && "(solo disponible si la fecha de pago es antes del vencimiento)"}
            </label>
            {cuotaForm.aplicarDescuento && (
              <div className="bg-emerald-400/10 border border-emerald-400/20 rounded-xl p-3">
                <Row>
                  <Field label="Descuento (%)" half>
                    <input type="number" step="0.1" min={0} max={100} className={INPUT} value={cuotaForm.descuento_pct}
                      onChange={e => setCuotaForm(p => ({ ...p, descuento_pct: e.target.value, descuento_monto: "" }))} placeholder="5" />
                  </Field>
                  <Field label="o monto fijo (Gs.)" half>
                    <input type="number" className={INPUT} value={cuotaForm.descuento_monto}
                      onChange={e => setCuotaForm(p => ({ ...p, descuento_monto: e.target.value, descuento_pct: "" }))} placeholder="50000" />
                  </Field>
                </Row>
                <p className="text-xs text-emerald-300 mt-2">
                  Descuento: {fmtGs(descuentoMonto)} · Neto a cobrar: {fmtGs(montoExigido)}
                </p>
              </div>
            )}

            {/* Escenario A: aviso de cobro parcial */}
            {seraParcial && (
              <div className="bg-amber-400/10 border border-amber-400/20 rounded-xl px-3 py-2 text-xs text-amber-300">
                Cobro parcial: quedará un saldo de {fmtGs(saldoResultante)} que se trasladará a la cuota N°{cuota.numero + 1 <= modalCuota.venta.n_cuotas ? cuota.numero + 1 : "nueva"}, con interés punitorio
                {modalCuota.venta.tasa_punitoria_mensual ? ` (${modalCuota.venta.tasa_punitoria_mensual}% mensual)` : " (esta venta no tiene tasa punitoria configurada, no se sumará interés)"}.
              </div>
            )}

            <Row>
              <Field label="N° de factura / recibo (opcional)" half>
                <input className={INPUT} value={cuotaForm.factura_numero} onChange={e => setCuotaForm(p => ({ ...p, factura_numero: e.target.value }))} placeholder="001-001-0000001" />
              </Field>
              <Field label="Notas / observaciones" half>
                <input className={INPUT} value={cuotaForm.notas} onChange={e => setCuotaForm(p => ({ ...p, notas: e.target.value }))} />
              </Field>
            </Row>

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalCuota(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleCobrarCuota} disabled={cuotaFacturando} className={BTN_PRI}>
                {cuotaFacturando ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Registrar cobro</>}
              </button>
            </div>
          </div>
        </Modal>
        );
      })()}

      {/* ── MODAL: Detalle venta ─────────────────────────────────────────────── */}
      {modalVentaDetalle && (
        <Modal title={`Detalle Venta — Mza ${modalVentaDetalle.lote_manzana} Lote ${modalVentaDetalle.lote_numero}`} icon={<Eye className="w-5 h-5 text-blue-400" />} onClose={() => setModalVentaDetalle(null)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["Loteamiento", modalVentaDetalle.loteamiento_nombre],
                ["Cliente", modalVentaDetalle.cliente_nombre],
                ["RUC", modalVentaDetalle.cliente_ruc || "—"],
                ["Fecha venta", modalVentaDetalle.fecha_venta],
                ["Monto total", fmtGs(modalVentaDetalle.monto_total)],
                ["Monto financiado", fmtGs(modalVentaDetalle.monto_financiado)],
                ["N° cuotas", modalVentaDetalle.n_cuotas],
                ["Tasa mensual", `${modalVentaDetalle.tasa_interes_mensual || 0}%`],
                ["Tasa punitoria (mora)", `${modalVentaDetalle.tasa_punitoria_mensual || 0}%`],
                ["Seña aplicada", fmtGs(modalVentaDetalle.monto_seña_aplicado)],
                ["Cuenta", modalVentaDetalle.cuenta_nombre || "—"],
              ].map(([k, v]) => (
                <div key={k} className="bg-white/5 rounded-lg px-3 py-2">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">{k}</div>
                  <div className="text-slate-200 font-medium">{v}</div>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Cuotas</p>
              <div className="max-h-60 overflow-y-auto rounded-lg border border-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-white/5 text-slate-400">
                    <tr>
                      <th className="text-left px-3 py-2">N°</th>
                      <th className="text-left px-3 py-2">Vence</th>
                      <th className="text-right px-3 py-2">Capital</th>
                      <th className="text-right px-3 py-2">Cuota</th>
                      <th className="text-left px-3 py-2">Estado</th>
                      <th className="text-left px-3 py-2">Fecha pago</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {(modalVentaDetalle.cuotas || []).map(c => (
                      <tr key={c.numero} className="hover:bg-white/5">
                        <td className="px-3 py-1.5 text-slate-300">{c.numero}</td>
                        <td className="px-3 py-1.5 text-slate-400">{c.fecha_vencimiento}</td>
                        <td className="px-3 py-1.5 text-right text-slate-300">{fmtNum(c.monto_capital)}</td>
                        <td className="px-3 py-1.5 text-right text-emerald-300">{fmtNum(c.monto_cuota)}</td>
                        <td className="px-3 py-1.5"><CuotaBadge c={c} /></td>
                        <td className="px-3 py-1.5 text-slate-500">{c.fecha_pago || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {(modalVentaDetalle.historial_titularidad || []).length > 0 && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Trazabilidad — Traspasos de cuenta</p>
                <div className="space-y-2">
                  {modalVentaDetalle.historial_titularidad.map(h => (
                    <div key={h.id} className="bg-white/5 rounded-lg px-3 py-2 text-xs">
                      <div className="flex items-center justify-between text-slate-300">
                        <span>{h.comprador_anterior?.nombre || "—"} → <span className="text-blue-300 font-medium">{h.comprador_nuevo?.nombre || "—"}</span></span>
                        <span className="text-slate-500">{h.fecha_traspaso}</span>
                      </div>
                      <div className="text-slate-500 mt-1">
                        Saldo traspasado: {fmtGs(h.saldo_pendiente_traspasado)} · {h.cuotas_traspasadas} cuota(s){h.recalculado ? " · recalculado" : ""}
                        {h.motivo ? ` · ${h.motivo}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(modalVentaDetalle.historial_refinanciaciones || []).length > 0 && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Trazabilidad — Refinanciaciones</p>
                <div className="space-y-2">
                  {modalVentaDetalle.historial_refinanciaciones.map(h => (
                    <div key={h.id} className="bg-white/5 rounded-lg px-3 py-2 text-xs">
                      <div className="flex items-center justify-between text-slate-300">
                        <span>Cuotas N°{h.cuotas_origen.map(c => c.numero).join(", ")} → {h.n_cuotas_nuevas} cuota(s) nueva(s) (N°{h.numeros_cuotas_nuevas.join(", ")})</span>
                        <span className="text-slate-500">{h.fecha}</span>
                      </div>
                      <div className="text-slate-500 mt-1">
                        Monto base: {fmtGs(h.monto_base_refinanciado)} · Tasa: {h.tasa_interes_refinanciacion}% · Por {h.usuario || "—"}
                        {h.motivo ? ` · ${h.motivo}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canGestionarVentas && modalVentaDetalle.estado === "activo" && (modalVentaDetalle.cuotas || []).some(c => c.estado !== "pagada") && (
              <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                <button
                  onClick={() => { const v = modalVentaDetalle; setModalVentaDetalle(null); abrirRefinanciar(v); }}
                  className="text-xs px-3 py-1.5 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 text-violet-300 rounded-lg transition-colors"
                >
                  Refinanciar deuda
                </button>
                <button
                  onClick={() => { const v = modalVentaDetalle; setModalVentaDetalle(null); abrirTraspaso(v); }}
                  className="text-xs px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-300 rounded-lg transition-colors"
                >
                  Traspaso de cuenta
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ── MODAL: Traspaso de Cuenta ────────────────────────────────────────── */}
      {modalTraspaso && (
        <Modal title={`Traspaso de Cuenta — Mza ${modalTraspaso.lote_manzana} Lote ${modalTraspaso.lote_numero}`} icon={<Users className="w-5 h-5 text-blue-400" />} onClose={() => setModalTraspaso(null)} wide>
          <div className="space-y-4">
            <div className="bg-blue-400/10 border border-blue-400/20 rounded-xl px-4 py-3 text-sm text-blue-300">
              Deudor actual: <strong>{modalTraspaso.cliente_nombre}</strong> · Saldo pendiente: {fmtGs((modalTraspaso.cuotas || []).filter(c => !["pagada", "parcial", "anulada_por_refinanciacion"].includes(c.estado)).reduce((s, c) => s + (c.monto_cuota || 0), 0))}
            </div>
            <ClienteLoteadoraSelect
              clientes={clientes}
              token={token}
              logo={slug}
              onClienteCreado={upsertClienteLocal}
              label="Cliente destino (nuevo deudor) *"
              value={{
                id: traspasoForm.cliente_destino_id,
                nombre: traspasoForm.cliente_destino_nombre,
                ruc: traspasoForm.cliente_destino_documento,
              }}
              onChange={cliente => setTraspasoForm(p => ({
                ...p,
                cliente_destino_id: cliente.id || "",
                cliente_destino_nombre: cliente.nombre || "",
                cliente_destino_documento: cliente.ruc || "",
              }))}
            />
            <Row>
              <Field label="Fecha de traspaso" half><DateInput className={INPUT} value={traspasoForm.fecha_traspaso} onChange={e => setTraspasoForm(p => ({ ...p, fecha_traspaso: e.target.value }))} /></Field>
              <Field label="Motivo" half><input className={INPUT} value={traspasoForm.motivo} onChange={e => setTraspasoForm(p => ({ ...p, motivo: e.target.value }))} placeholder="Venta a tercero, cesión de derechos..." /></Field>
            </Row>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={traspasoForm.recalcular} onChange={e => setTraspasoForm(p => ({ ...p, recalcular: e.target.checked }))} />
              Recalcular monto de cuotas y vencimientos para el nuevo deudor
            </label>
            {traspasoForm.recalcular && (
              <div className="bg-white/5 rounded-xl p-3 space-y-3">
                <Row>
                  <Field label="N° de cuotas restantes" half>
                    <input type="number" min={1} max={360} className={INPUT} value={traspasoForm.n_cuotas_restantes}
                      onChange={e => setTraspasoForm(p => ({ ...p, n_cuotas_restantes: e.target.value }))}
                      placeholder={String((modalTraspaso.cuotas || []).filter(c => c.estado !== "pagada").length)} />
                  </Field>
                  <Field label="Tasa mensual % (0 = sin interés)" half>
                    <input type="number" step="0.1" min={0} className={INPUT} value={traspasoForm.nueva_tasa_interes_mensual}
                      onChange={e => setTraspasoForm(p => ({ ...p, nueva_tasa_interes_mensual: e.target.value }))}
                      placeholder={String(modalTraspaso.tasa_interes_mensual || 0)} />
                  </Field>
                </Row>
                <Field label="Nueva fecha de inicio de cuotas">
                  <DateInput className={INPUT} value={traspasoForm.nueva_fecha_inicio} onChange={e => setTraspasoForm(p => ({ ...p, nueva_fecha_inicio: e.target.value }))} />
                </Field>
                <p className="text-xs text-slate-500">Se recalcula sobre el saldo de capital pendiente. Las cuotas ya pagadas no se modifican.</p>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalTraspaso(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleTraspaso} disabled={saving} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Confirmar traspaso</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL: Refinanciación de Deuda ───────────────────────────────────── */}
      {modalRefinanciar && (() => {
        const hoyStr = hoy();
        const seleccionables = cuotasRefinanciables(modalRefinanciar).sort((a, b) => a.numero - b.numero);
        const montoBase = montoBaseRefinanciado(modalRefinanciar, refinanciarForm.numeros_cuotas);
        return (
        <Modal
          title={`Refinanciar Deuda — Mza ${modalRefinanciar.lote_manzana} Lote ${modalRefinanciar.lote_numero}`}
          icon={<TrendingUp className="w-5 h-5 text-violet-400" />}
          onClose={() => setModalRefinanciar(null)}
          wide
        >
          <div className="space-y-4">
            <div className="bg-violet-400/10 border border-violet-400/20 rounded-xl px-4 py-3 text-sm text-violet-300">
              Deudor actual: <strong>{modalRefinanciar.cliente_nombre}</strong>
            </div>

            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">
                Seleccioná las cuotas a refinanciar (vencidas o por vencer)
              </p>
              <div className="max-h-56 overflow-y-auto rounded-lg border border-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-white/5 text-slate-400 sticky top-0">
                    <tr>
                      <th className="px-3 py-2"></th>
                      <th className="text-left px-3 py-2">N°</th>
                      <th className="text-left px-3 py-2">Vence</th>
                      <th className="text-left px-3 py-2">Estado</th>
                      <th className="text-right px-3 py-2">Cuota</th>
                      <th className="text-right px-3 py-2">Aporta al refinanciar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {seleccionables.map(c => {
                      const esVencida = (c.fecha_vencimiento || hoyStr) < hoyStr;
                      const aporte = esVencida ? (c.monto_cuota || 0) : Math.max(0, (c.monto_cuota || 0) - (c.monto_interes || 0));
                      const checked = refinanciarForm.numeros_cuotas.includes(c.numero);
                      return (
                        <tr key={c.numero} className={`hover:bg-white/5 cursor-pointer ${checked ? "bg-violet-500/5" : ""}`} onClick={() => toggleCuotaRefinanciar(c.numero)}>
                          <td className="px-3 py-1.5"><input type="checkbox" checked={checked} onChange={() => toggleCuotaRefinanciar(c.numero)} onClick={e => e.stopPropagation()} /></td>
                          <td className="px-3 py-1.5 text-slate-300">{c.numero}</td>
                          <td className="px-3 py-1.5 text-slate-400">{c.fecha_vencimiento}</td>
                          <td className="px-3 py-1.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${esVencida ? "bg-rose-400/10 text-rose-400" : "bg-white/5 text-slate-400"}`}>
                              {esVencida ? "Vencida" : "Por vencer"}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-right text-slate-300">{fmtGs(c.monto_cuota)}</td>
                          <td className="px-3 py-1.5 text-right text-violet-300 font-medium">
                            {fmtGs(aporte)}{!esVencida && <span className="text-slate-600 font-normal"> (solo capital)</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {seleccionables.length === 0 && (
                      <tr><td colSpan={6} className="text-center py-4 text-slate-500">No hay cuotas disponibles para refinanciar</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Las cuotas por vencer aportan solo su capital: el interés que tenían proyectado se excluye porque el nuevo plan calcula interés propio sobre el monto base.
              </p>
            </div>

            <div className="bg-white/5 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
              <span className="text-slate-400">Monto Base Refinanciado</span>
              <span className="text-violet-300 font-semibold text-base">{fmtGs(montoBase)}</span>
            </div>

            <Row>
              <Field label="Tasa de interés de refinanciación % mensual *" half>
                <input type="number" step="0.1" min={0} className={INPUT} value={refinanciarForm.tasa_interes_refinanciacion}
                  onChange={e => setRefinanciarForm(p => ({ ...p, tasa_interes_refinanciacion: e.target.value }))} placeholder="0.0" />
              </Field>
              <Field label="N° de cuotas nuevas *" half>
                <input type="number" min={1} max={360} className={INPUT} value={refinanciarForm.n_cuotas_nuevas}
                  onChange={e => setRefinanciarForm(p => ({ ...p, n_cuotas_nuevas: e.target.value }))} />
              </Field>
            </Row>
            <Row>
              <Field label="Fecha de inicio del nuevo plan" half>
                <DateInput className={INPUT} value={refinanciarForm.fecha_inicio_nuevas}
                  onChange={e => setRefinanciarForm(p => ({ ...p, fecha_inicio_nuevas: e.target.value }))} />
              </Field>
              <Field label="Motivo (opcional)" half>
                <input className={INPUT} value={refinanciarForm.motivo} onChange={e => setRefinanciarForm(p => ({ ...p, motivo: e.target.value }))} placeholder="Cuotas vencidas acumuladas, pedido del cliente..." />
              </Field>
            </Row>

            {refinanciarPreviewLoading && (
              <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Calculando nuevo plan...</div>
            )}
            {!refinanciarPreviewLoading && refinanciarPreview.length > 0 && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Nuevo plan de pagos (vista previa)</p>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5 text-slate-400 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">N°</th>
                        <th className="text-left px-3 py-2">Vence</th>
                        <th className="text-right px-3 py-2">Capital</th>
                        <th className="text-right px-3 py-2">Interés</th>
                        <th className="text-right px-3 py-2">Cuota</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {refinanciarPreview.map(c => (
                        <tr key={c.numero}>
                          <td className="px-3 py-1.5 text-slate-300">{c.numero}</td>
                          <td className="px-3 py-1.5 text-slate-400">{c.fecha_vencimiento}</td>
                          <td className="px-3 py-1.5 text-right text-slate-300">{fmtNum(c.monto_capital)}</td>
                          <td className="px-3 py-1.5 text-right text-slate-400">{fmtNum(c.monto_interes)}</td>
                          <td className="px-3 py-1.5 text-right text-emerald-300 font-medium">{fmtGs(c.monto_cuota)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setModalRefinanciar(null)} className={BTN_SEC}>Cancelar</button>
              <button onClick={handleRefinanciar} disabled={saving || refinanciarForm.numeros_cuotas.length === 0} className={BTN_PRI}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Confirmar refinanciación</>}
              </button>
            </div>
          </div>
        </Modal>
        );
      })()}

      {/* ── MODAL: Ficha del Lote (trazabilidad) ─────────────────────────────── */}
      {modalLoteFicha && (
        <Modal title={`Ficha — Mza ${modalLoteFicha.manzana} Lote ${modalLoteFicha.numero}`} icon={<FileText className="w-5 h-5 text-blue-400" />} onClose={() => setModalLoteFicha(null)} wide>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["Loteamiento", modalLoteFicha.loteamiento_nombre],
                ["Estado", ESTADO_LABEL[modalLoteFicha.estado] || modalLoteFicha.estado],
                ["CCC", modalLoteFicha.ccc || "—"],
                ["Finca Madre", modalLoteFicha.finca_madre || "—"],
                ["Matrícula", modalLoteFicha.matricula || "—"],
                ["Padrón", modalLoteFicha.padron || "—"],
                ["Superficie", modalLoteFicha.superficie_m2 ? `${modalLoteFicha.superficie_m2} m²` : "—"],
                ["Precio Contado (Gs.)", fmtGs(modalLoteFicha.precio_lista)],
                ["Precio Contado (USD)", modalLoteFicha.precio_contado_usd ? `US$ ${Number(modalLoteFicha.precio_contado_usd).toLocaleString("es-PY")}` : "—"],
                ["Entrega mínima (Gs.)", fmtGs(modalLoteFicha.entrega_minima)],
                ["Entrega mínima (USD)", modalLoteFicha.entrega_minima_usd ? `US$ ${Number(modalLoteFicha.entrega_minima_usd).toLocaleString("es-PY")}` : "—"],
                ["Precio Financiado (Gs.)", modalLoteFicha.precio_financiado_gs ? fmtGs(modalLoteFicha.precio_financiado_gs) : "—"],
                ["Precio Financiado (USD)", modalLoteFicha.precio_financiado_usd ? `US$ ${Number(modalLoteFicha.precio_financiado_usd).toLocaleString("es-PY")}` : "—"],
              ].map(([k, v]) => (
                <div key={k} className="bg-white/5 rounded-lg px-3 py-2">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">{k}</div>
                  <div className="text-slate-200 font-medium">{v}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-lg px-3 py-2">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">Comprador original</div>
                <div className="text-slate-200 font-medium">{modalLoteFicha.comprador_original?.nombre || "—"}</div>
                {modalLoteFicha.comprador_original?.documento && (
                  <div className="text-xs text-slate-500">{modalLoteFicha.comprador_original.documento}</div>
                )}
              </div>
              <div className="bg-blue-400/10 border border-blue-400/20 rounded-lg px-3 py-2">
                <div className="text-[10px] text-blue-300 uppercase tracking-wider">Deudor actual</div>
                <div className="text-blue-200 font-medium">{modalLoteFicha.deudor_actual?.nombre || modalLoteFicha.cliente_nombre || "—"}</div>
                {modalLoteFicha.deudor_actual?.documento && (
                  <div className="text-xs text-blue-300/70">{modalLoteFicha.deudor_actual.documento}</div>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">
                Historial de traspasos {(modalLoteFicha.historial_traspasos || []).length === 0 && "— sin traspasos registrados"}
              </p>
              {(modalLoteFicha.historial_traspasos || []).length > 0 && (
                <div className="space-y-2">
                  {modalLoteFicha.historial_traspasos.map(h => (
                    <div key={h.id} className="bg-white/5 rounded-lg px-3 py-2 text-xs">
                      <div className="flex items-center justify-between text-slate-300">
                        <span>{h.comprador_anterior?.nombre || "—"} → <span className="text-blue-300 font-medium">{h.comprador_nuevo?.nombre || "—"}</span></span>
                        <span className="text-slate-500">{h.fecha_traspaso}</span>
                      </div>
                      <div className="text-slate-500 mt-1">
                        Por {h.usuario || "—"} · Saldo traspasado: {fmtGs(h.saldo_pendiente_traspasado)} · {h.cuotas_traspasadas} cuota(s){h.recalculado ? " · recalculado" : ""}
                        {h.motivo ? ` · ${h.motivo}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(modalLoteFicha.historial_refinanciaciones || []).length > 0 && (
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Historial de refinanciaciones</p>
                <div className="space-y-2">
                  {modalLoteFicha.historial_refinanciaciones.map(h => (
                    <div key={h.id} className="bg-white/5 rounded-lg px-3 py-2 text-xs">
                      <div className="flex items-center justify-between text-slate-300">
                        <span>Cuotas N°{h.cuotas_origen.map(c => c.numero).join(", ")} → {h.n_cuotas_nuevas} cuota(s) nueva(s)</span>
                        <span className="text-slate-500">{h.fecha}</span>
                      </div>
                      <div className="text-slate-500 mt-1">
                        Por {h.usuario || "—"} · Monto base: {fmtGs(h.monto_base_refinanciado)} · Tasa: {h.tasa_interes_refinanciacion}%
                        {h.motivo ? ` · ${h.motivo}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canGestionarVentas && modalLoteFicha.estado === "vendido" && (
              <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                <button
                  onClick={() => {
                    const venta = ventas.find(v => v.lote_id === modalLoteFicha.id && v.estado === "activo");
                    if (!venta) { toast.error("No hay una venta activa con saldo pendiente para este lote"); return; }
                    setModalLoteFicha(null);
                    abrirRefinanciar(venta);
                  }}
                  className="text-xs px-3 py-1.5 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 text-violet-300 rounded-lg transition-colors"
                >
                  Refinanciar deuda
                </button>
                <button
                  onClick={() => {
                    const venta = ventas.find(v => v.lote_id === modalLoteFicha.id && v.estado === "activo");
                    if (!venta) { toast.error("No hay una venta activa con saldo pendiente para este lote"); return; }
                    setModalLoteFicha(null);
                    abrirTraspaso(venta);
                  }}
                  className="text-xs px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-300 rounded-lg transition-colors"
                >
                  Traspaso de cuenta
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Sub-componentes ────────────────────────────────────────────────────────────

function LoteCard({ lote, canGestionar, canReservar, canGestionarVentas, onEdit, onDelete, onReservar, onVender, onFicha }) {
  const fmtGs = (n) => `Gs. ${(Number(n) || 0).toLocaleString("es-PY")}`;
  return (
    <div className={`bg-arandu-dark-light border rounded-xl p-4 space-y-3 transition-all hover:border-white/20 ${
      lote.estado === "disponible" ? "border-emerald-400/20" : lote.estado === "reservado" ? "border-amber-400/20" : "border-rose-400/20"
    }`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400">{lote.loteamiento_nombre}</p>
          <p className="text-base font-bold text-white">Mza {lote.manzana} — N°{lote.numero}</p>
          <p className="text-xs text-slate-500">{lote.tipo}</p>
        </div>
        <EstadoBadge estado={lote.estado} />
      </div>

      <div className="text-emerald-300 font-semibold">{fmtGs(lote.precio_lista)}</div>

      {lote.superficie_m2 > 0 && (
        <p className="text-xs text-slate-500">{lote.superficie_m2} m²</p>
      )}
      {lote.ccc && (
        <p className="text-xs text-slate-600 font-mono">CCC: {lote.ccc}</p>
      )}
      {(lote.matricula || lote.padron) && (
        <p className="text-xs text-slate-600 font-mono">
          {lote.matricula && <>Matrícula: {lote.matricula}</>}
          {lote.matricula && lote.padron && " · "}
          {lote.padron && <>Padrón: {lote.padron}</>}
        </p>
      )}
      {lote.cliente_nombre && lote.estado !== "disponible" && (
        <p className="text-xs text-slate-400 truncate">Cliente: {lote.cliente_nombre}</p>
      )}

      {/* Acciones */}
      <div className="flex flex-wrap gap-2 pt-1">
        {lote.estado !== "disponible" && (
          <button onClick={onFicha} className="text-xs px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-lg transition-colors flex items-center gap-1">
            <Eye className="w-3 h-3" /> Ficha
          </button>
        )}
        {lote.estado === "disponible" && canReservar && (
          <button onClick={onReservar} className="text-xs px-2.5 py-1 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-300 rounded-lg transition-colors">
            Reservar
          </button>
        )}
        {(lote.estado === "disponible" || lote.estado === "reservado") && canGestionarVentas && (
          <button onClick={onVender} className="text-xs px-2.5 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-300 rounded-lg transition-colors">
            Vender
          </button>
        )}
        {canGestionar && lote.estado === "disponible" && (
          <>
            <button onClick={onEdit} className="text-xs px-2.5 py-1 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-300 rounded-lg transition-colors">
              Editar
            </button>
            <button onClick={onDelete} className="text-xs px-2.5 py-1 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-300 rounded-lg transition-colors">
              Eliminar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function VentaRow({ venta, canCobrar, canGestionarVentas, onCobrar, onCancelar, onDetalle, onTraspaso, onRefinanciar }) {
  const [expanded, setExpanded] = useState(false);
  const fmtGs = (n) => `Gs. ${(Number(n) || 0).toLocaleString("es-PY")}`;
  const hoyStr = new Date().toISOString().split("T")[0];
  const cuotas = asArray(venta.cuotas); // incluye anuladas por refinanciación (se muestran tachadas en la tabla)
  const cuotasActivas = cuotas.filter(c => c.estado !== "anulada_por_refinanciacion");
  const pendientes = cuotasActivas.filter(c => !["pagada", "parcial"].includes(c.estado));
  const vencidas = cuotasActivas.filter(c => !["pagada", "parcial"].includes(c.estado) && c.fecha_vencimiento < hoyStr);
  const pagadas = cuotasActivas.filter(c => c.estado === "pagada");
  const pct = cuotasActivas.length > 0 ? Math.round((pagadas.length / cuotasActivas.length) * 100) : 0;

  const estadoColor = VENTA_ESTADO_COLOR[venta.estado] || "text-slate-400 bg-slate-400/10 border-slate-400/20";

  return (
    <div className="bg-arandu-dark-light border border-white/10 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">
              Mza {venta.lote_manzana} Lote {venta.lote_numero} — {venta.loteamiento_nombre}
            </p>
            <p className="text-xs text-slate-400">{venta.cliente_nombre} · {venta.fecha_venta}</p>
          </div>
          <div className="hidden sm:flex items-center gap-3 shrink-0">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${estadoColor}`}>{venta.estado}</span>
            {vencidas.length > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-400/10 border border-rose-400/20 text-rose-400 font-semibold">
                {vencidas.length} vencida{vencidas.length > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          <div className="text-right hidden md:block">
            <div className="text-xs text-slate-400">{pagadas.length}/{cuotas.length} cuotas · {pct}%</div>
            <div className="text-emerald-300 text-sm font-medium">{fmtGs(venta.monto_total)}</div>
          </div>
          <button onClick={onDetalle} className="text-slate-400 hover:text-white p-1.5">
            <Eye className="w-4 h-4" />
          </button>
          <button onClick={() => setExpanded(p => !p)} className="text-slate-400 hover:text-white p-1.5">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Barra de progreso */}
      <div className="px-4 pb-2">
        <div className="w-full bg-white/10 rounded-full h-1.5">
          <div className="bg-emerald-400 rounded-full h-1.5 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Cuotas expandidas */}
      {expanded && (
        <div className="border-t border-white/10 max-h-80 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/5 text-slate-400 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2">N°</th>
                <th className="text-left px-4 py-2">Vence</th>
                <th className="text-right px-4 py-2">Cuota</th>
                <th className="text-left px-4 py-2">Estado</th>
                <th className="text-left px-4 py-2">Pago</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {cuotas.map(c => (
                <tr key={c.numero} className="hover:bg-white/5">
                  <td className="px-4 py-2 text-slate-300">{c.numero}</td>
                  <td className={`px-4 py-2 ${c.estado !== "pagada" && c.fecha_vencimiento < hoyStr ? "text-rose-400" : "text-slate-400"}`}>{c.fecha_vencimiento}</td>
                  <td className="px-4 py-2 text-right text-emerald-300 font-medium">{fmtGs(c.monto_cuota)}</td>
                  <td className="px-4 py-2"><CuotaBadge c={c} /></td>
                  <td className="px-4 py-2 text-slate-500">{c.fecha_pago || "—"}</td>
                  <td className="px-4 py-2 text-right">
                    {!["pagada", "parcial", "anulada_por_refinanciacion"].includes(c.estado) && canCobrar && venta.estado !== "cancelado" && (
                      <button
                        onClick={() => onCobrar(c)}
                        className="text-[10px] px-2 py-1 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-300 rounded-lg transition-colors"
                      >
                        Cobrar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Acciones de venta */}
      {canGestionarVentas && venta.estado === "activo" && (
        <div className="border-t border-white/10 px-4 py-2 flex justify-end gap-2">
          {pendientes.length > 0 && (
            <button onClick={onRefinanciar} className="text-[10px] px-2.5 py-1 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/20 text-violet-300 rounded-lg transition-colors">
              Refinanciar deuda
            </button>
          )}
          {pendientes.length > 0 && (
            <button onClick={onTraspaso} className="text-[10px] px-2.5 py-1 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-300 rounded-lg transition-colors">
              Traspaso de cuenta
            </button>
          )}
          <button onClick={onCancelar} className="text-[10px] px-2.5 py-1 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-400 rounded-lg transition-colors">
            Cancelar venta
          </button>
        </div>
      )}
    </div>
  );
}
